import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Save, FileText, ChevronDown, ChevronUp, Trash2, Printer, X, Building2, FileCheck,
    ClipboardCheck, Anchor, Package, Truck, Warehouse, DollarSign, User, Ship, MapPin, Settings,
    Container, Download, Calendar, Plus, Route, Bell, Shield, Eye, Copy, Hash,
    Check, Upload, Weight, Ruler, Paperclip, Edit2, FileSignature, AlertCircle, Thermometer,
    Send,
    FilePenLine
} from 'lucide-react';
import { useDispatch, useSelector } from "react-redux";
import { toast } from 'sonner';
import RoutingInformation from '@/components/OceanImport/RoutingInformation';
import PartiesInformation from '@/components/OceanImport/PartiesInformation';

import { Input } from '@/components/ui/input';
import ARAPChargesContainer from './charges/ARAPChargesContainer';
import { SearchBar } from '@/components/SearchBox/SearchBar';
import { Button } from '@/components/ui/button';
import DateInputwithcal from '@/components/ui/DateInputwithcal';
import { addImportBookingDetails, getImportBookingDetails, updateImportBookingDetails } from '@/redux/ImportBooking/actions';
import { callLoader } from '@/redux/Loader/actions';
import { showToast } from '@/redux/Toast/actions';
import { getClauselist } from '@/redux/Common/actions';
import { getAddressAPI } from '@/redux/Purchase/actions';
import { SearchBarPagination } from '@/components/SearchBox/SearchBarPagination';
import ImportBookingValidation from '@/validation/importBookingform.json';
import ImportBookingValidationDraft from '@/validation/importBookingformdraft.json';

import { validateForm } from '@/components/ui/useFormValidation';
import notificationService from '@/services/notificationService';
import initialBooking from './initialBooking.json';
import FileUpload from '@/components/FileUpload/FileUpload';
import UploadNotes from '@/components/NotesUpload/NotesUpload';
import CelebrationButton from '@/components/ui/CelebrationButton/CelebrationButton';
import PrintDocumentsSelectionModal from '@/components/PrintDocuments/PrintDocumentsSelectionModal';
import PageHeader from '@/components/FormHeader/formPageHeader';

import {
    ROUTING_FORM_FIELD_KEYS,
    PARTIES_FORM_FIELD_KEYS,
    buildSliceSignature,
    pickFormFields,
} from './importBookingFormSlices';

/** Stable identity for `RoutingInformation` memo (avoid inline new array each render). */
const ROUTING_SECTION_REQUIRED_FIELDS = ['carrier', 'motherVessel', 'portOfLoading', 'portOfDischarge', 'etdAtPol'];

const appendBlLetterhead = (url, blPrintOnLetterhead) => {
    if (!blPrintOnLetterhead) return url;
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}blLetterhead=1`;
};

const ImportBookingForm = () => {
    const navigate = useNavigate();
    const { id: idbooking } = useParams();
    const isEditMode = !!idbooking && idbooking !== 'new';
    const dispatch = useDispatch();

    const [authUser] = JSON.parse(localStorage.getItem('authUser') ?? "[{}]");
    const userName = authUser?.username;
    const companyId = authUser?.companyId;

    /** Undefined until `_setNewBooking` (initialBooking) or `_getBooking` (API JSON) runs — avoids showing template data on edit. */
    const [formData, setFormData] = useState(undefined);

    // AR/AP Charges State
    const [arCharges, setArCharges] = useState([]);
    const [apCharges, setApCharges] = useState([]);

    const [rows, setRows] = useState([]);

    const [dateErrors, setDateErrors] = useState({});

    const { isAnimate } = useSelector((state) => state.toast);

    const { countryList, documentTypePurchaseList, documentTypeSalesList, incoTermsList, quantityTypeList, salesPersonList, uomTypeList,
        commodityGroupList, containerTypeList, operatorList, moveTypeList, currency_list, charges_basis_list, fileManagerList,
        officeTypeList, notesTypeList, packageTypeList, blTypeList, coloaderList } = useSelector((state) => state.common);

    // console.log("coloaderList", coloaderList)

    const [clauses, setClauses] = useState([]);
    const [originalFormClauses, setOriginalFormClauses] = useState([]);
    const [errors, setErrors] = useState({});
    const [printModalOpen, setPrintModalOpen] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);

    const stepSectionMap = [
        'fileInfo',
        'shipperInfo',
        'routingInfo',
        'partiesInfo',
        'cargoDetails',
        'mode',
        'Particulars',
        'rateCharges',
        'remarks',
        'commonClause',
        'notesUpload',
        'documentsUpload',
    ];
    const sectionRefs = {
        fileInfo: useRef(null),
        bookingInfo: useRef(null),
        shipperInfo: useRef(null),
        routingInfo: useRef(null),
        cargoDetails: useRef(null),
        rateCharges: useRef(null),
        commodity: useRef(null),
        remarks: useRef(null),
        commonClause: useRef(null),
        partiesInfo: useRef(null),
        Particulars: useRef(null),
        containers: useRef(null),
        mode: useRef(null),
        notesUpload: useRef(null),
        documentsUpload: useRef(null),
    };

    /** When true, form is hydrated; stays true while editing so effects do not re-run on every keystroke. */
    const formReady = formData != null;

    const formDataRef = useRef(formData);
    formDataRef.current = formData;

    const routingFieldsSig = buildSliceSignature(formData, ROUTING_FORM_FIELD_KEYS);
    const routingFormSlice = useMemo(
        () => pickFormFields(formDataRef.current, ROUTING_FORM_FIELD_KEYS),
        [routingFieldsSig]
    );

    const partiesFieldsSig = buildSliceSignature(formData, PARTIES_FORM_FIELD_KEYS);
    const partiesFormSlice = useMemo(
        () => pickFormFields(formDataRef.current, PARTIES_FORM_FIELD_KEYS),
        [partiesFieldsSig]
    );

    const importPrintDocuments = useMemo(() => {
        if (!formData?.BolID || Number(formData.BolID) <= 0) return [];
        return [
            {
                id: 'arrival_notice',
                name: 'ARRIVAL NOTICE (FREIGHTED)',
                remarks: `Arrival Notice — File: ${formData.FileRefNumber || '—'}`,
                defaultSelected: true,
            },
            {
                id: 'arrival_notice_non_freighted',
                name: 'ARRIVAL NOTICE (NON FREIGHTED)',
                remarks: `Arrival Notice (Non Freighted) — File: ${formData.FileRefNumber || '—'}`,
                defaultSelected: false,
            },
            {
                id: 'release_order',
                name: 'RELEASE ORDER',
                remarks: `Release Order — File: ${formData.FileRefNumber || '—'}`,
                defaultSelected: false,
            },
            {
                id: 'hbl_original_freighted',
                name: 'HBL ORIGINAL FREIGHTED',
                remarks: formData.HBLNumber ? `HBL: ${formData.HBLNumber}` : '',
                defaultSelected: false,
            },
            {
                id: 'isf_worksheet',
                name: 'ISF WORKSHEET',
                remarks: formData.FileRefNumber ? `ISF — File: ${formData.FileRefNumber}` : 'Importer Security Filing summary',
                defaultSelected: false,
            },
            {
                id: 'delivery_order',
                name: 'DELIVERY ORDER',
                remarks: `Delivery Order - File: ${formData?.FileRefNumber || '—'}`,
                defaultSelected: false,
            },
            {
                id: 'rate_quote',
                name: 'RATE QUOTE',
                remarks:
                    formData.RateQuoteNumber
                        ? `Quote: ${formData.RateQuoteNumber}`
                        : Number(formData.RateQuoteID) > 0
                            ? `Quote ID: ${formData.RateQuoteID}`
                            : 'No rate quote linked',
                highlightRow: true,
                defaultSelected: false,
            },
        ];
    }, [formData]);



    const handleImportPrintDocuments = useCallback(
        ({ items, blPrintOnLetterhead }) => {
            const bolId = formData?.BolID;
            if (!bolId || Number(bolId) <= 0) {
                toast.error('Booking ID missing.');
                return;
            }
            const fileRef = formData?.FileRefNumber || '';
            let delayMs = 0;
            let scheduled = 0;
            const openStaggered = (url) => {
                const finalUrl = appendBlLetterhead(url, blPrintOnLetterhead);
                scheduled += 1;
                window.setTimeout(() => {
                    window.open(finalUrl, '_blank', 'noopener,noreferrer');
                }, delayMs);
                delayMs += 350;
            };

            for (const item of items) {
                let path = null;
                switch (item.id) {
                    case 'arrival_notice':
                        path = `/import-booking/Print/${btoa(String(bolId))}`;
                        break;
                    case 'arrival_notice_non_freighted':
                        path = `/import-booking/PrintNonFreight/${btoa(String(bolId))}`;
                        break;
                    case 'release_order':
                        path = `/release-order/print/${btoa(String(bolId))}?fileRefNumber=${encodeURIComponent(fileRef)}`;
                        break;
                    case 'hbl_original_freighted':
                        path = `/import-hbl-print/${btoa(String(bolId))}`;
                        break;
                    case 'isf_worksheet':
                        path = `/import-isf-print/${btoa(String(bolId))}`;
                        break;
                    case 'delivery_order':
                        path = `/delivery-order/print/${btoa(String(bolId))}`;
                        break;
                    case 'rate_quote': {
                        const rq = formData?.RateQuoteID;
                        if (!rq || Number(rq) <= 0) {
                            toast.error('No import rate quote linked — link a quote first.');
                            continue;
                        }
                        path = `/import-rate-quote/print/${btoa(String(rq))}`;
                        break;
                    }

                    default:
                        toast.error(`Unknown document type: ${item.name}`);
                        continue;
                }
                const copies = Math.max(1, Number(item.copies) || 1);
                for (let c = 0; c < copies; c++) {
                    openStaggered(path);
                }
            }

            if (scheduled > 0) {
                toast.success('Opening print / PDF in new tab(s). Allow pop-ups if asked.');
                setPrintModalOpen(false);
            } else {
                toast.error('No print views were opened. Check linked rate quote or try again.');
            }
        },
        [formData]
    );

    // Form state

    useEffect(() => {
        if (idbooking != 0 && idbooking != null && idbooking != undefined && idbooking != 'new' && idbooking != '') {
            _getBooking();

        } else {
            _setNewBooking();
        }
    }, [idbooking])


    const _getBooking = async (overrideBolId) => {
        const bolKey =
            overrideBolId !== undefined && overrideBolId !== null && overrideBolId !== ''
                ? overrideBolId
                : idbooking;
        if (
            bolKey == null ||
            bolKey === undefined ||
            bolKey === '' ||
            bolKey === 'new' ||
            bolKey === 0 ||
            bolKey === '0'
        ) {
            return;
        }
        try {
            dispatch(callLoader(1));
            const resultData = await dispatch(getImportBookingDetails(bolKey, authUser?.companyId));
            console.log("data will come:", resultData);
            setFormData(resultData);

            setCargoItems(resultData?.BolCargoDimensionsData ?? [defaultCargoItem]);
            getClauselistData(resultData?.BolClausesData || []);
            setOriginalFormClauses(resultData?.BolClausesData || []);
            const ensureRowIds = (rows) => (rows || []).map((r) => ({
                ...r,
                rowid: r?.rowid || (typeof crypto !== 'undefined' && crypto.randomUUID
                    ? crypto.randomUUID()
                    : `row-${Date.now()}-${Math.random().toString(36).slice(2)}`),
            }));
            setArCharges(ensureRowIds(resultData?.BolArChargesData));
            setApCharges(ensureRowIds(resultData?.BolApChargesData));
            setRows(resultData?.BOLDescriptionsData ?? [initialrow])

            setFormData(prev => ({
                ...prev,
                ModifiedBy: `${userName ?? ''}`.trim(),
                COMPANY_ID: companyId

            }));
            handleEmpltyApiData(resultData);
        } catch (error) {
            dispatch(showToast("Failed to load booking details", "error"))
        } finally {
            dispatch(callLoader(0));
        }


    }

    const _setNewBooking = async () => {

        setFormData({
            ...initialBooking,
            CreatedBy: userName,
            COMPANY_ID: companyId,
            CreatedOn: new Date().toISOString(),
        });
        console.log("companyid", companyId)
        setCargoItems([defaultCargoItem]);
        getClauselistData([]);
        setArCharges([]);
        setApCharges([]);

        handleEmpltyApiData(initialBooking);
        setRows([initialrow])
    }

    /** Only normalise office/shipment defaults — do not replace the whole form (that was wiping CreatedBy / routing merges). */
    const handleEmpltyApiData = (data) => {
        setFormData((prev) => {
            const p = prev && typeof prev === 'object' ? prev : { ...initialBooking };
            const office = data?.OfficeType;
            const ship = data?.ShipmentType;
            return {
                ...p,
                OfficeType: office && String(office).trim() ? office : (p?.OfficeType || 'WISCONSIN'),
                ShipmentType: ship && String(ship).trim() ? ship : (p?.ShipmentType || 'FCL'),
            };
        });
    };

    // Cargo items for LCL/RO-RO
    const defaultCargoItem = {
        CARGO_DIMENSION_ID: 0,
        PIECE: 0,
        LENGTH: 0,
        WIDTH: 0,
        HEIGHT: 0,
        PACKAGE_TYPE: '',
        DIMENSION_TYPE: 'METER',
        WEIGHT_KGS: 0,
        CBM: 0,
        CFT: 0,
        WEIGHT_LBS: 0,
        ISDELETED: false,
    };

    const [cargoItems, setCargoItems] = useState([{ ...defaultCargoItem }]);

    const addCargoRow = () => {
        setCargoItems(prev => [...prev, { ...defaultCargoItem }]);
    };

    const removeCargoRow = (index) => {
        setCargoItems(prev => {
            const newItems = [...prev];
            if (newItems[index]) newItems[index].ISDELETED = true;
            return newItems;
        });
    };

    const handleCargoItemChange = (index, field, value) => {
        setCargoItems(prevItems => {
            const newItems = [...prevItems];
            if (newItems[index]?.isDeleted) return prevItems;

            const updatedItem = { ...newItems[index], [field]: value };

            if (formData?.autoCalculate !== false) {
                let length = parseFloat(updatedItem.LENGTH) || 0;
                let width = parseFloat(updatedItem.WIDTH) || 0;
                let height = parseFloat(updatedItem.HEIGHT) || 0;

                if (updatedItem.DIMENSION_TYPE === 'INCH') {
                    length *= 0.0254;
                    width *= 0.0254;
                    height *= 0.0254;
                } else if (updatedItem.DIMENSION_TYPE === 'CM') {
                    length *= 0.01;
                    width *= 0.01;
                    height *= 0.01;
                }

                const piece = parseFloat(updatedItem.piece) || 1;
                const cbm = length * width * height * piece;
                const cft = cbm * 35.3147;
                const weightKgs = parseFloat(updatedItem.WEIGHT_KGS) || 0;
                const weightLbs = weightKgs * 2.20462;

                updatedItem.CBM = cbm.toFixed(2);
                updatedItem.CFT = cft.toFixed(2);
                updatedItem.WEIGHT_LBS = weightLbs.toFixed(2);
            }

            newItems[index] = updatedItem;
            return newItems;
        });
    };

    const activeItems = cargoItems.filter(item => !item.ISDELETED);
    const totalPieces = activeItems.reduce((sum, item) => sum + (parseFloat(item.PIECE) || 0), 0);
    const totalWeightKgs = activeItems.reduce((sum, item) => sum + (parseFloat(item.WEIGHT_KGS) || 0), 0);
    const totalWeightLbs = activeItems.reduce((sum, item) => sum + (parseFloat(item.WEIGHT_LBS) || 0), 0);
    const totalVolumeCbm = activeItems.reduce((sum, item) => sum + (parseFloat(item.CBM) || 0), 0);
    const totalVolumeCft = activeItems.reduce((sum, item) => sum + (parseFloat(item.CFT) || 0), 0);
    const density = totalVolumeCbm > 0 ? (totalWeightKgs / totalVolumeCbm).toFixed(2) : '0.00';
    const finalTotals = {
        totalPieces,
        totalWeightKgs: totalWeightKgs.toFixed(2),
        totalWeightLbs: totalWeightLbs.toFixed(2),
        totalVolumeCbm: totalVolumeCbm.toFixed(2),
        totalVolumeCft: totalVolumeCft.toFixed(2),
        density
    };


    const [expandedSections, setExpandedSections] = useState({
        fileInfo: true,
        shipperInfo: true,
        routingInfo: true,
        bookingInfo: true,
        cargoDetails: true,
        commodity: true,
        remarks: true,
        commonClause: true,
        partiesInfo: true,
        Particulars: true,
        containers: true,
        mode: true,
        notesUpload: true,
        documentsUpload: true,
        rateCharges: true,
    });


    const toggleSection = (section) => {
        setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
    };

    const handleInputChange = useCallback((e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    }, []);




    const scrollToSection = (index) => {
        const sectionKey = stepSectionMap[index];
        const ref = sectionRefs[sectionKey];
        if (ref?.current) {
            ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            // Expand the section
            setExpandedSections(prev => ({
                ...prev,
                [sectionKey]: true,
                ...(sectionKey === 'fileInfo' ? { bookingInfo: true } : {})
            }));
        }
    };

    useEffect(() => {
        if (!formReady) return;

        let cancelled = false;
        const observers = [];

        const attach = () => {
            if (cancelled) return;
            stepSectionMap.forEach((key, index) => {
                const ref = sectionRefs[key];
                if (!ref?.current) return;

                const observer = new IntersectionObserver(
                    ([entry]) => {
                        if (entry.isIntersecting) {
                            setCurrentStep(index);
                        }
                    },
                    {
                        root: null,
                        rootMargin: '-30% 0px -60% 0px',
                        threshold: 0,
                    }
                );

                observer.observe(ref.current);
                observers.push(observer);
            });
        };

        const rafId = requestAnimationFrame(attach);

        return () => {
            cancelled = true;
            cancelAnimationFrame(rafId);
            observers.forEach((o) => o.disconnect());
        };
    }, [formReady, idbooking]);

    const steps = [
        'File & Booking Info',
        'Shipper & Consignee',
        'Routing Info',
        'Parties Info',
        'Cargo Details',
        'HBL / Release / Reefer',
        'Particulars',
        'AR / AP Charges',
        'Remarks',
        'Clause',
        'Notes',
        'Documents',
    ];



    /* -------------------- EFFECT -------------------- */
    const initialrow = {
        Id: 0,
        ContainerNumber: "",
        SealNumber: "",
        MarksNo: "",

        ContTypeCode: "",
        ContTypeName: "",

        NoPackage: 1,
        PackageCode: "",
        PackageDescription: "",
        ScheduleB: "",

        HAZCode: "",
        Manufacturer: "",
        Country: "",
        CargoDescription: "",

        KGS: "0",
        LBS: "0",
        CBM: "0",
        CFT: "0",

        NetKgs: 0,
        NetLbs: 0,

        IsDeleted: false,
        IsActive: true,

        ITNumber: "",
        CNCPRSNumber: "",
        PickUpNumber: "",

        LastDate: new Date().toISOString(),

        AmsCode: "",
        Shipperpono: "",

        InGateDate: null,
        Gateinfull: null,
        Gateinempty: null,

        DescEDICode: ""
    };

    const calculateTotals = useCallback(() => {
        let totals = {
            containers: 0,
            packages: 0,
            netKgs: 0,
            netLbs: 0,
            kgs: 0,
            lbs: 0,
            cbm: 0,
            cft: 0
        };

        rows
            .filter(r => r.IsDeleted === false)
            .forEach(r => {
                totals.containers += 1;
                totals.packages += Number(r.NoPackage) || 0;
                totals.netKgs += Number(r.NetKgs) || 0;
                totals.netLbs += Number(r.NetLbs) || 0;
                totals.kgs += Number(r.KGS) || 0;
                totals.lbs += Number(r.LBS) || 0;
                totals.cbm += Number(r.CBM) || 0;
                totals.cft += Number(r.CFT) || 0;
            });

        setFormData((prev) => {
            if (prev == null) return prev;
            return {
                ...prev,
                TotalContainer: totals.containers,
                TotalPackage: totals.packages,
                TOTAL_NET_KGS: Number(totals.netKgs).toFixed(2),
                TOTAL_NET_LBS: Number(totals.netLbs).toFixed(2),
                TotalKgs: Number(totals.kgs).toFixed(2),
                TotalLbs: Number(totals.lbs).toFixed(2),
                TotalCbm: Number(totals.cbm).toFixed(2),
                TotalCft: Number(totals.cft).toFixed(2),
            };
        });
    }, [rows]);

    useEffect(() => {
        if (!formReady) return;
        calculateTotals();
    }, [formReady, calculateTotals]);

    const _handleInputChange = (index, field, value) => {
        const updated = [...rows];
        updated[index][field] = value;
        setRows(updated);
    };

    const KG_TO_LBS = 2.20462;
    const LBS_TO_KG = 0.453592;
    const CBM_TO_CFT = 35.3147;
    const CFT_TO_CBM = 0.0283168;

    const handleWeightChange = (rowIndex, field, value) => {
        const num = parseFloat(value);

        setRows(prevRows => {
            const updatedRows = [...prevRows];
            const row = { ...updatedRows[rowIndex], [field]: value };

            if (!isNaN(num)) {
                switch (field) {
                    case 'NetKgs':
                        row.NetLbs = (num * KG_TO_LBS).toFixed(2);
                        break;

                    case 'NetLbs':
                        row.NetKgs = (num * LBS_TO_KG).toFixed(2);
                        break;

                    case 'KGS':
                        row.LBS = ((num * KG_TO_LBS).toFixed(2)).toString();
                        break;

                    case 'LBS':
                        row.KGS = ((num * LBS_TO_KG).toFixed(2)).toString();
                        break;

                    case 'CBM':
                        row.CFT = ((num * CBM_TO_CFT).toFixed(2)).toString();
                        break;

                    case 'CFT':
                        row.CBM = ((num * CFT_TO_CBM).toFixed(2)).toString();
                        break;

                    default:
                        break;
                }
            }

            updatedRows[rowIndex] = row;
            return updatedRows;
        });
    };



    const addRow = () => {
        setRows(prev => {
            const nextId = prev.length + 1;

            return [
                ...prev,
                {
                    ...initialrow,
                    ID: nextId,
                    ContainerNumber: "",
                    IsDeleted: false,
                    InGateDate: new Date().toISOString()
                }
            ];
        });
    };


    const handleDateChange = useCallback((name, value) => {
        setFormData((prev) => ({ ...prev, [name]: value ? value : null }));
    }, []);

    const handleDateError = useCallback((fieldName, hasError) => {
        setDateErrors(prev => ({
            ...prev,
            [fieldName]: hasError
        }));
    }, []);

    // start common clauses handlers
    const syncClausesWithFormData = (apiClauses, formClauses) => {
        if (!apiClauses || apiClauses.length === 0) return apiClauses;
        if (!formClauses || formClauses.length === 0) {
            return apiClauses.map(item => ({
                ...item,
                applied: false,
                id: null,
                bookingId: null
            }));
        }
        return apiClauses.map(apiClause => {
            const existingClause = formClauses.find(
                f => f.CLAUSES_TYPE_DESCRIPTION_ID == apiClause.ClauseTypeDescriptionID
            );

            return {
                ...apiClause,
                applied: existingClause ? existingClause.IS_ACTIVE : false,
                id: existingClause ? existingClause.ID : null,
                bookingId: existingClause ? existingClause.BOL_ID : null
            };
        });
    };

    const getClauselistData = async (formClauses = []) => {
        try {
            const payload = {
                IPageNo: 1,
                IPageRecords: 1000,
                Type: 3
            };

            const clauseData = await dispatch(getClauselist(payload));

            const transformedData = clauseData.map(item => ({
                ClauseTypeDescriptionID: item.ClauseTypeDescriptionID,
                ClauseType: item.ClauseType,
                ClauseDescription: item.ClauseDescription,
                applied: false,
                id: null,
                bookingId: null
            }));

            const syncedClauses = syncClausesWithFormData(
                transformedData,
                formClauses
            );

            setClauses(syncedClauses);

        } catch (error) {
            console.error('Error loading clauses:', error);
        }
    };

    const handleClauseChange = (clauseId, field, value) => {
        if (field === 'applied') {
            setClauses(prevClauses =>
                prevClauses.map(clause => {
                    if (clause.ClauseTypeDescriptionID === clauseId) {
                        const newApplied = typeof value === 'boolean' ? value : !clause.applied;

                        return {
                            ...clause,
                            applied: newApplied,
                            id: 0,
                            bookingId: newApplied ? (clause.bookingId || formData?.id || 0) : clause.bookingId
                        };
                    }
                    return clause;
                })
            );
        }
    };

    const prepareClausesForAPI = () => {
        const payloadMap = new Map();

        clauses?.forEach(clause => {
            if (clause.applied === true) {
                payloadMap.set(clause.ClauseTypeDescriptionID, {
                    ID: clause.id || 0,
                    CLAUSES_TYPE_DESCRIPTION_ID: clause.ClauseTypeDescriptionID,
                    IS_ACTIVE: true
                });
            }
        });

        originalFormClauses?.forEach(oldClause => {
            const stillApplied = clauses?.some(
                c =>
                    c.ClauseTypeDescriptionID === oldClause.clausesTypeDescriptionId &&
                    c.applied === true
            );

            if (!stillApplied) {
                payloadMap.set(oldClause.clausesTypeDescriptionId, {
                    ID: oldClause.id || 0,
                    CLAUSES_TYPE_DESCRIPTION_ID: oldClause.clausesTypeDescriptionId || 0,
                    IS_ACTIVE: false
                });
            }
        });

        return Array.from(payloadMap.values());
    };
    {/* end common clauses handlers */ }

    const isChargeEmpty = (charge) => {
        return (
            !charge.descriptions &&
            !charge.currency &&
            charge.sellAmount === 0 &&
            charge.buyAmount === 0 &&
            charge.quantity === 0
        );
    };

    const _setAddress = useCallback(async (type, id) => {
        const address = await getAddressAPI(id);
        setFormData(prev => ({ ...prev, [`${type}`]: address }));
    }, []);

    const getSalesPersonName = (id) => {
        return salesPersonList?.find(
            (v) => v.salesPersonId === id
        )?.salesPerson || "";
    };

    const handleCopyFields = useCallback((checkboxName, sourceKey, targets) => {
        setFormData((prev) => {
            if (!prev) return prev;
            const checked = !prev[checkboxName];
            const next = { ...prev, [checkboxName]: checked };

            if (checked) {
                targets.forEach((t) => {
                    next[t.name] = prev[t.valueKey] || '';
                });
            } else {
                targets.forEach((t) => {
                    next[t.name] = t.clearValue ?? '';
                });
            }
            return next;
        });
    }, []);

    const _getPortCode = (strdata) => {
        if (!strdata) return "";
        const match = strdata.match(/\[(.*?)\]/);
        const code = match ? match[1] : "";
        return code;
    }

    const handleSubmit = async (e) => {
        let submitPayload = null;
        try {
            dispatch(callLoader(1));

            const cleanedCargoItems = cargoItems.filter(item => {
                return !(
                    item.piece === 0 &&
                    item.length === 0 &&
                    item.width === 0 &&
                    item.height === 0
                );
            });
            const cleanedArCharges = arCharges.filter(charge => charge.IS_ACTIVE === true && charge.DESCRIPTION.trim() !== "" && charge.AMOUNT.trim() !== "");
            const cleanedApCharges = apCharges.filter(charge => charge.IS_ACTIVE === true && charge.DESCRIPTION.trim() !== "" && charge.AMOUNT.trim() !== "");
            const payload = {
                ...formData,
                COMPANY_ID: authUser?.companyId,
                BolClausesData: prepareClausesForAPI(),
                BolArChargesData: cleanedArCharges,
                BolApChargesData: cleanedApCharges,
                BolCargoDimensionsData: cleanedCargoItems,
                DescriptionCommodityLinesData: [],
                BOLDescriptionsData: rows
            }
            submitPayload = payload;

            console.log('submit api payload:', payload);

            if (e && e.preventDefault) e.preventDefault();
            const validationErrors = validateForm({
                data: formData,
                rules: ImportBookingValidation,
            });

            setErrors(validationErrors);

            if (Object.keys(validationErrors).length > 0) {
                return;
            }

            if (Object.values(dateErrors).some(Boolean)) {
                return;
            }


            console.log('Payload:', payload);


            let response;
            let insertedBolId = null;

            const isExistingBooking =
                idbooking &&
                idbooking !== 0 &&
                idbooking !== '0' &&
                idbooking !== 'new';

            if (isExistingBooking) {
                response = await updateImportBookingDetails(payload);

            } else {
                response = await addImportBookingDetails(payload);
                console.log('Add Import Booking Response:', response);

                const resultString = response?.result || "";

                const bookingIdMatch = resultString.match(/BolID\s*:\s*(\d+)/);
                const fileNumberMatch = resultString.match(/fileRefNumber\s*:\s*([A-Z0-9]+)/);

                const bookingId = bookingIdMatch ? bookingIdMatch[1] : null;
                const fileNumber = fileNumberMatch ? fileNumberMatch[1] : null;
                insertedBolId = bookingId;

                setFormData(prev => {
                    console.log('Parsed Booking ID:', bookingId, 'File Number:', fileNumber);
                    const parsedId = parseInt(bookingId, 10);

                    return {
                        ...prev,
                        BolID: isNaN(parsedId) ? 0 : parsedId,
                        modifiedBy: `${authUser?.firstName ?? ''} ${authUser?.lastName ?? ''}`.trim(),
                        companyId: authUser?.companyId,
                        FileRefNumber: fileNumber
                    };
                });

            }

            console.log('Import booking API final response object:', response);

            if (response?.statuscode === 200 || response?.statuscode === 201) {
                const refreshBolId = isExistingBooking ? idbooking : insertedBolId;
                await _getBooking(refreshBolId ?? undefined);

                const isNewBooking = !isExistingBooking;
                const fileNumber = formData?.FileRefNumber || payload?.FileRefNumber;
                const customerName = formData?.CustomerName || payload?.CustomerName;

                if (isNewBooking) {
                    notificationService.bookingCreated(fileNumber, customerName);
                    dispatch(showToast("Import Booking created successfully!", "success", true))

                } else {
                    notificationService.bookingStatusChanged(fileNumber, 'Updated');
                    dispatch(showToast("Import Booking updated successfully!", "success", true))

                }

                return {
                    success: true,
                    data: response.data,
                };
            } else {
                console.error('❌ Import booking returned non-success statuscode', {
                    statuscode: response?.statuscode,
                    message: response?.message,
                    response,
                });
                dispatch(showToast(response?.message || "Error Generated While Updating Booking", "error"))
            }

            return {
                success: false,
                status: response?.status,
            };

        } catch (error) {
            console.error('❌ Import booking submit failed', {
                endpoint: idbooking && idbooking !== 0 && idbooking !== '0' && idbooking !== 'new'
                    ? 'v1/Arrival_Import/UpdateArrivalNotice'
                    : 'v1/Arrival_Import/InsertArrivalNotice',
                status: error?.response?.status,
                statusText: error?.response?.statusText,
                responseData: error?.response?.data,
                requestUrl: error?.config?.url,
                requestMethod: error?.config?.method,
                payloadPreview: submitPayload ? {
                    BolID: submitPayload?.BolID,
                    COMPANY_ID: submitPayload?.COMPANY_ID,
                    FileRefNumber: submitPayload?.FileRefNumber,
                    HBLNumber: submitPayload?.HBLNumber,
                    ShipmentType: submitPayload?.ShipmentType,
                    FileManager: submitPayload?.FileManager,
                    SalesPersonID: submitPayload?.SalesPersonID,
                    cargoCount: submitPayload?.BolCargoDimensionsData?.length || 0,
                    descriptionCount: submitPayload?.BOLDescriptionsData?.length || 0,
                    arCount: submitPayload?.BolArChargesData?.length || 0,
                    apCount: submitPayload?.BolApChargesData?.length || 0,
                } : null,
            });
            dispatch(showToast(error?.response?.data?.title ||
                error?.message ||
                "Something went wrong", "error"))

            return {
                success: false,
                status: error?.response?.status,
                errors: error?.response?.data?.errors,
            };
        }
        finally {
            dispatch(callLoader(0));
        }
    };

    const handleSaveDraft = async (e) => {
        let submitPayload = null;
        try {
            dispatch(callLoader(1));

            const cleanedCargoItems = cargoItems.filter(item => {
                return !(
                    item.piece === 0 &&
                    item.length === 0 &&
                    item.width === 0 &&
                    item.height === 0
                );
            });

            const cleanedArCharges = arCharges.filter(charge => charge.IS_ACTIVE === true && charge.DESCRIPTION.trim() !== "" && charge.AMOUNT.trim() !== "");
            const cleanedApCharges = apCharges.filter(charge => charge.IS_ACTIVE === true && charge.DESCRIPTION.trim() !== "" && charge.AMOUNT.trim() !== "");
            const payload = {
                ...formData,
                COMPANY_ID: authUser?.companyId,
                BolClausesData: prepareClausesForAPI(),
                BolArChargesData: cleanedArCharges,
                BolApChargesData: cleanedApCharges,
                BolCargoDimensionsData: cleanedCargoItems,
                DescriptionCommodityLinesData: [],
                BOLDescriptionsData: rows
            }
            submitPayload = payload;

            console.log('submit api payload:', payload);

            if (e && e.preventDefault) e.preventDefault();
            const validationErrors = validateForm({
                data: formData,
                rules: ImportBookingValidationDraft,
            });

            setErrors(validationErrors);

            if (Object.keys(validationErrors).length > 0) {
                return;
            }

            if (Object.values(dateErrors).some(Boolean)) {
                return;
            }


            console.log('Payload:', payload);


            let response;
            let insertedBolId = null;

            const isExistingBooking =
                idbooking &&
                idbooking !== 0 &&
                idbooking !== '0' &&
                idbooking !== 'new';

            if (isExistingBooking) {
                response = await updateImportBookingDetails(payload);

            } else {
                response = await addImportBookingDetails(payload);
                console.log('Add Import Booking Response:', response);

                const resultString = response?.result || "";

                const bookingIdMatch = resultString.match(/BolID\s*:\s*(\d+)/);
                const fileNumberMatch = resultString.match(/fileRefNumber\s*:\s*([A-Z0-9]+)/);

                const bookingId = bookingIdMatch ? bookingIdMatch[1] : null;
                const fileNumber = fileNumberMatch ? fileNumberMatch[1] : null;
                insertedBolId = bookingId;

                setFormData(prev => {
                    console.log('Parsed Booking ID:', bookingId, 'File Number:', fileNumber);
                    const parsedId = parseInt(bookingId, 10);

                    return {
                        ...prev,
                        BolID: isNaN(parsedId) ? 0 : parsedId,
                        modifiedBy: `${authUser?.firstName ?? ''} ${authUser?.lastName ?? ''}`.trim(),
                        companyId: authUser?.companyId,
                        FileRefNumber: fileNumber
                    };
                });

            }

            console.log('Import booking API final response object:', response);

            if (response?.statuscode === 200 || response?.statuscode === 201) {
                const refreshBolId = isExistingBooking ? idbooking : insertedBolId;
                await _getBooking(refreshBolId ?? undefined);

                const isNewBooking = !isExistingBooking;
                const fileNumber = formData?.FileRefNumber || payload?.FileRefNumber;
                const customerName = formData?.CustomerName || payload?.CustomerName;

                if (isNewBooking) {
                    notificationService.bookingCreated(fileNumber, customerName);
                    dispatch(showToast("Import Booking saved as draft successfully!", "success", true))

                } else {
                    notificationService.bookingStatusChanged(fileNumber, 'Updated');
                    dispatch(showToast("Import Booking updated as draft successfully!", "success", true))

                }

                return {
                    success: true,
                    data: response.data,
                };
            } else {
                console.error('❌ Import booking returned non-success statuscode', {
                    statuscode: response?.statuscode,
                    message: response?.message,
                    response,
                });
                dispatch(showToast(response?.message || "Error Generated While Updating Booking", "error"))
            }

            return {
                success: false,
                status: response?.status,
            };

        } catch (error) {
            console.error('❌ Import booking submit failed', {
                endpoint: idbooking && idbooking !== 0 && idbooking !== '0' && idbooking !== 'new'
                    ? 'v1/Arrival_Import/UpdateArrivalNotice'
                    : 'v1/Arrival_Import/InsertArrivalNotice',
                status: error?.response?.status,
                statusText: error?.response?.statusText,
                responseData: error?.response?.data,
                requestUrl: error?.config?.url,
                requestMethod: error?.config?.method,
                payloadPreview: submitPayload ? {
                    BolID: submitPayload?.BolID,
                    COMPANY_ID: submitPayload?.COMPANY_ID,
                    FileRefNumber: submitPayload?.FileRefNumber,
                    HBLNumber: submitPayload?.HBLNumber,
                    ShipmentType: submitPayload?.ShipmentType,
                    FileManager: submitPayload?.FileManager,
                    SalesPersonID: submitPayload?.SalesPersonID,
                    cargoCount: submitPayload?.BolCargoDimensionsData?.length || 0,
                    descriptionCount: submitPayload?.BOLDescriptionsData?.length || 0,
                    arCount: submitPayload?.BolArChargesData?.length || 0,
                    apCount: submitPayload?.BolApChargesData?.length || 0,
                } : null,
            });
            dispatch(showToast(error?.response?.data?.title ||
                error?.message ||
                "Something went wrong", "error"))

            return {
                success: false,
                status: error?.response?.status,
                errors: error?.response?.data?.errors,
            };
        }
        finally {
            dispatch(callLoader(0));
        }
    };

    const SECTION_COLORS = {
        blue: { icon: 'bg-blue-500' },
        teal: { icon: 'bg-teal-500' },
        violet: { icon: 'bg-violet-500' },
        cyan: { icon: 'bg-cyan-500' },
        green: { icon: 'bg-green-500' },
        indigo: { icon: 'bg-indigo-500' },
        amber: { icon: 'bg-amber-500' },
        orange: { icon: 'bg-orange-500' },
        rose: { icon: 'bg-rose-500' },
        dark: { icon: 'bg-slate-700' },
        red: { icon: 'bg-red-500' },
        pink: { icon: 'bg-pink-500' },
    };

    const SectionHeader = ({ icon: Icon, title, section, badge, action, color = 'blue' }) => {
        const c = SECTION_COLORS[color] ?? SECTION_COLORS.blue;
        const isDark = color === 'dark';

        return (
            <button
                onClick={() => toggleSection(section)}
                className="w-full flex items-center justify-between p-4 bg-gradient-to-r to-slate-100 dark:from-slate-800 dark:to-slate-700 cursor-pointer"
            >
                <div className="flex items-center gap-3">
                    <div className={`p-2 mx-2 rounded-lg shadow-md ${c.icon}`}>
                        <Icon className="w-4 h-4 text-white" />
                    </div>
                    <h2 className={`text-sm font-semibold ${isDark ? 'text-slate-100' : 'text-slate-900 dark:text-slate-200'}`}>
                        {title}
                    </h2>
                    {badge && (
                        <span className="px-2 py-0.5 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 text-xs font-medium rounded-full">
                            {badge}
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {action}
                    <div className={`p-1.5 rounded-md transition-colors ${expandedSections[section] ? 'bg-indigo-100 dark:bg-indigo-900/30' : 'bg-slate-200 dark:bg-slate-700'}`}>
                        {expandedSections[section] ? (
                            <ChevronUp className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                        ) : (
                            <ChevronDown className="w-4 h-4 text-slate-500 dark:text-slate-400" />
                        )}
                    </div>
                </div>
            </button>
        );
    };

    const inputClass = "w-full px-3 py-[0.6rem] bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md text-sm text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all";
    const selectClass = "w-full px-3 py-[0.5rem] bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all appearance-none cursor-pointer";
    const labelClass = "block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1";

    const isSectionFilled = useCallback((key) => {
        if (!formData) return false;
        const checks = {
            fileInfo: !!(formData.HBLNumber && formData.FileManager),
            shipperInfo: !!(formData.Shipper && formData.Consignee && formData.BillToParty),
            routingInfo: !!(formData.CarrierName && formData.PortOfLoading),
            partiesInfo: !!(formData.BillToParty),
            cargoDetails: cargoItems.filter(i => !i.ISDELETED).some(i => Number(i.PIECE) > 0),
            mode: !!(formData.HBLPaymentMode || formData.HBLReleaseMode),
            Particulars: rows.filter(r => !r.IsDeleted).some(r => r.ContainerNumber),
            rateCharges: arCharges.length > 0 || apCharges.length > 0,
            remarks: !!(formData.Freight_Payable || formData.PlaceOfIssue || formData.IssueDate),
            commonClause: clauses.some(c => c.applied),
            notesUpload: false,
            documentsUpload: false,
        };
        return checks[key] ?? false;
    }, [formData, cargoItems, rows, arCharges, apCharges, clauses]);



    return (
        <div className="mx-auto p-2 sm:p-3 sticky top-0 bg-gradient-to-r">
            {/* PageHeader with proper class for targeting */}
            <div className="page-header-sticky sticky top-0 z-50">
                <PageHeader
                    icon={<Download className="w-5 h-5 text-white" />}
                    title="Import Booking Information"
                    // badge={isEditMode ? 'Editing Import Booking' : 'New Import Booking'}
                    subtitle={
                        isEditMode ? (
                            <>
                                {'File Reference '}
                                <span className="text-slate-700 font-semibold dark:text-slate-300">
                                    {formData?.FileRefNumber}
                                </span>
                            </>
                        ) : (
                            'Create and manage import shipment'
                        )
                    }
                    actions={[
                        { label: 'Cancel', icon: <X />, onClick: () => navigate('/import-booking'), variant: 'ghost' },
                        ...(isEditMode
                            ? [
                                {
                                    label: 'Print',
                                    icon: <Printer />,
                                    onClick: () => setPrintModalOpen(true),
                                    variant: 'tertiary',
                                },
                                '|',
                                {
                                    label: 'Release Order',
                                    icon: <FileCheck />,
                                    onClick: () =>
                                        navigate(`/release-order/new?bolid=${idbooking}&file_ref_no=${formData?.FileRefNumber}`),
                                    variant: 'tertiary',
                                },
                                {
                                    label: 'Delivery Order',
                                    icon: <Truck />,
                                    onClick: () => navigate(`/delivery-order/edit/${idbooking}`),
                                    variant: 'tertiary',
                                },
                                {
                                    label: 'Status Audit',
                                    icon: <Eye />,
                                    onClick: () =>
                                        navigate(`/import-booking/status-audit/${formData?.BolID || idbooking || 0}?companyId=${authUser?.companyId}`, {
                                            state: {
                                                bookingId: formData?.BolID || idbooking,
                                                FILENUMBER: formData?.FileRefNumber,
                                                companyId: authUser?.companyId,
                                                customerName: formData?.CustomerName || formData?.BookingPartyName || formData?.ImporterName || formData?.Consignee,
                                                createdBy: formData?.CreatedBy,
                                                createdOn: formData?.CreatedOn,
                                                carrierBookingNumber: formData?.CarrierBookingNo,
                                            },
                                        }),
                                    variant: 'black',
                                }

                            ]
                            : []),
                        {
                            label: 'Save Draft',
                            icon: <FilePenLine />,
                            onClick: handleSaveDraft,
                            variant: 'tertiary',
                        },
                        '|',

                    ]}
                    onSubmit={handleSubmit}
                />
            </div>
            <div style={{ height: 'var(--page-header-height)' }} />

            {/* Progress Indicator */}
            <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-700/30 dark:border-slate-700 px-4 py-3 m-2 overflow-x-auto">
                <div className="flex items-center min-w-max gap-0">
                    {steps.map((step, index) => {
                        const sectionKey = stepSectionMap[index];
                        const filled = isSectionFilled(sectionKey);
                        const done = filled && index !== currentStep;
                        const active = index === currentStep;

                        return (
                            <div key={step} className="flex items-center">
                                <div
                                    className={[
                                        'flex items-center gap-2 h-8 pl-2 pr-3 rounded-full text-xs font-medium cursor-pointer transition-all duration-200 select-none whitespace-nowrap',
                                        done && 'text-green-700 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20',
                                        active && 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
                                        !done && !active && 'text-slate-400 dark:text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800',
                                    ].filter(Boolean).join(' ')}
                                    onClick={() => {
                                        scrollToSection(index);
                                    }}
                                >
                                    <span
                                        className={[
                                            'w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-semibold flex-shrink-0',
                                            done && 'bg-green-500 text-white',
                                            active && 'bg-blue-600 text-white',
                                            !done && !active && 'bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400',
                                        ].filter(Boolean).join(' ')}
                                    >
                                        {done ? (
                                            <Check className="w-3 h-3" />
                                        ) : (
                                            index + 1
                                        )}
                                    </span>
                                    <span className="hidden sm:inline">{step}</span>
                                </div>
                                {index < steps.length - 1 && (
                                    <div
                                        className={[
                                            'w-4 h-0.5 flex-shrink-0 mx-1 rounded-full transition-colors duration-300',
                                            done
                                                ? 'bg-green-400 dark:bg-green-600'
                                                : 'bg-slate-200 dark:bg-slate-700',
                                        ].join(' ')}
                                    />
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            <form className="space-y-4 m-2 mt-4" onSubmit={(e) => e.preventDefault()}>
                {/* File Information & Booking Information */}
                <div className='grid grid-cols-1 xl:grid-cols-2 gap-6'>
                    {/* File Information */}
                    <div ref={sectionRefs.fileInfo} className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-700/30 dark:border-slate-700 overflow-hidden hover:shadow-md dark:shadow-none dark:hover:shadow-[0_0_12px_rgba(59,130,246,0.25)] transition-shadow duration-200">
                        <SectionHeader icon={FileText} title="File Information" color="cyan" section="fileInfo" />

                        {expandedSections.fileInfo && (
                            <div className="p-4 space-y-3 border-t border-slate-200 dark:border-slate-800">
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-3">
                                    <div>
                                        <label className={labelClass}>File Ref Number <span className="text-red-500">*</span></label>
                                        <input type="text" name="FileRefNumber" value={formData?.FileRefNumber} onChange={handleInputChange} className={`${inputClass} !bg-slate-100 dark:!bg-slate-700 `} readOnly />
                                    </div>
                                    <div>
                                        <label className={labelClass}>HBL Number <span className="text-red-500">*</span></label>
                                        <input type="text" name="HBLNumber" value={formData?.HBLNumber} onChange={handleInputChange} className={inputClass} />
                                        {errors.HBLNumber && <p className="text-red-500 text-xs mt-1">{errors.HBLNumber}</p>}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                                            File Manager:<span className="text-red-500">*</span>
                                        </label>
                                        <select className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-1 focus:ring-[#0072FF]"
                                            value={formData?.FileManager || ''}
                                            onChange={(e) => { handleInputChange(e); }}
                                            name="FileManager"
                                        >   <option value="">SELECT Manager</option>
                                            {
                                                fileManagerList.map((item, index) => (
                                                    <option key={index} value={item.id}>{item.filemanager}</option>
                                                ))
                                            }
                                        </select>
                                        {errors.FileManager && <p className="text-red-500 text-xs mt-1">{errors.FileManager}</p>}
                                    </div>
                                    <div>
                                        <label className={labelClass}>Customer PO Number</label>
                                        <input type="text" name="CustomerPoNo" value={formData?.CustomerPoNo} onChange={handleInputChange} className={inputClass} />
                                    </div>
                                    <div>
                                        <label className={labelClass}>Forwarder Ref. No.</label>
                                        <input type="text" name="ForwarderRefNo" value={formData?.ForwarderRefNo} onChange={handleInputChange} placeholder="Enter reference" className={inputClass} />
                                    </div>
                                    <div>
                                        <label className={labelClass}>File Closed Date</label>
                                        <DateInputwithcal onError={handleDateError} label="File Closed Date" name="FileClosedDate" value={formData?.FileClosedDate} onChange={handleDateChange} required />
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 pt-2 border-t border-slate-100 dark:border-slate-800">
                                    <div>
                                        <label className={labelClass}>AMS SCAC Code</label>
                                        <input type="text" name="AMS_SCACCode" value={formData?.AMS_SCACCode} onChange={handleInputChange} className={inputClass} />
                                    </div>
                                    <div>
                                        <label className={labelClass}>AMS House BL Number</label>
                                        <input type="text" name="AMSHBLNo" value={formData?.AMSHBLNo} onChange={handleInputChange} className={inputClass} />
                                    </div>
                                    <div>
                                        <label className={labelClass}>ISF Reference Number</label>
                                        <input type="text" name="IsfReferenceNumber" value={formData?.IsfReferenceNumber} onChange={handleInputChange} className={inputClass} placeholder="ISF Ref" />
                                    </div>
                                    <div>
                                        <label className={labelClass}>AMS Status</label>
                                        <input type="text" name="AMSStatus" value={formData?.AMSStatus} onChange={handleInputChange} className={inputClass} placeholder="Status" />
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-slate-100 dark:border-slate-800">
                                    <div>
                                        <label className={labelClass}>File Number Date</label>
                                        <DateInputwithcal onError={handleDateError} label="File Number Date" name="FileNumberDate" value={formData?.FileNumberDate} onChange={handleDateChange} required />
                                    </div>
                                    <div>
                                        <label className={labelClass}>AMS Reference Number <span className="text-amber-500">(internal)</span></label>
                                        <input type="text" name="AMSReferenceNumber" value={formData?.AMSReferenceNumber} onChange={handleInputChange} className={inputClass} placeholder="For internal use only" />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Booking Information */}
                    <div ref={sectionRefs.bookingInfo} className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-700/30 dark:border-slate-700 overflow-hidden hover:shadow-md dark:shadow-none dark:hover:shadow-[0_0_12px_rgba(59,130,246,0.25)] transition-shadow duration-200">
                        <SectionHeader icon={FileCheck} title="Booking Information" color="blue" section="bookingInfo" />

                        {expandedSections.bookingInfo && (
                            <div className="p-4 space-y-3 border-t border-slate-200 dark:border-slate-800">
                                <div className="flex flex-wrap gap-4 mb-2">
                                    <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400">
                                        <input type="checkbox" className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                                            checked={formData?.NRAShipments}
                                            onChange={handleInputChange}
                                            name="NRAShipments"
                                        />
                                        NRA Shipments
                                    </label>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                    <div>
                                        <label className={labelClass}>Office Type<span className="text-red-500">*</span></label>
                                        <select name="OfficeType" value={formData?.OfficeType} onChange={handleInputChange} className={selectClass}>
                                            <option value="">Select Office</option>
                                            {
                                                officeTypeList?.map((item, index) => (
                                                    <option key={index} value={item.officeCode}>{item.officeType}</option>
                                                ))
                                            }
                                        </select>
                                        {errors.OfficeType && <p className="text-red-500 text-xs mt-1">{errors.OfficeType}</p>}
                                    </div>
                                    <div>
                                        <label className={labelClass}>Shipping Type<span className="text-red-500">*</span></label>
                                        <select name="ShippingTerm" value={formData?.ShippingTerm} onChange={handleInputChange} className={selectClass}>
                                            <option value="FCL">FCL</option>
                                            <option value="LCL">LCL</option>
                                            <option value="RO-RO">RO-RO</option>
                                        </select>
                                        {errors.ShipmentType && <p className="text-red-500 text-xs mt-1">{errors.ShipmentType}</p>}
                                    </div>

                                    <div>
                                        <label className={labelClass}>MBL SCAC Code <span className="text-red-500">*</span></label>
                                        <input type="text" name="MBL_SCACCode" value={formData?.MBL_SCACCode} onChange={handleInputChange} className={inputClass} />
                                        {errors.MBL_SCACCode && <p className="text-red-500 text-xs mt-1">{errors.MBL_SCACCode}</p>}
                                    </div>
                                    <div>
                                        <label className={labelClass}>Master BL Number <span className="text-red-500">*</span></label>
                                        <input type="text" name="CarrierMBLNo" value={formData?.CarrierMBLNo} onChange={handleInputChange} className={inputClass} />
                                        {errors.CarrierMBLNo && <p className="text-red-500 text-xs mt-1">{errors.CarrierMBLNo}</p>}
                                    </div>
                                    <div>
                                        <label className={labelClass}>B/L Type <span className="text-red-500">*</span></label>
                                        <select name="BLType" value={formData?.BLType} onChange={handleInputChange} className={selectClass}>
                                            <option value="">Select BL Type</option>
                                            {
                                                blTypeList?.map((item, index) => (
                                                    <option key={index} value={item.blTypeName}>{item.blTypeName}</option>
                                                ))
                                            }
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">Inco Term:</label>
                                        <select className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-1 focus:ring-[#0072FF] "
                                            value={formData?.Incoterm || ''}
                                            name="Incoterm"
                                            onChange={(e) => {
                                                handleInputChange(e);
                                            }}
                                        >
                                            <option value="">SELECT</option>
                                            {
                                                incoTermsList?.map((term, key) => (
                                                    <option key={key} value={term?.id}>{term?.incoterm}</option>
                                                ))
                                            }
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                                            Sales Person: <span className='text-red-500'>*</span>
                                        </label>
                                        <select
                                            name="SalesPersonID"
                                            value={formData?.SalesPersonID}
                                            onChange={(e) => {
                                                const selectedOption = e.target.selectedOptions[0];
                                                const id = e.target.value;
                                                handleInputChange({ target: { name: 'SalesPersonID', value: id } });
                                            }}
                                            className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-1 focus:ring-[#0072FF]"
                                        >
                                            <option value="">Select</option>
                                            {salesPersonList?.map((v) => (
                                                <option
                                                    key={v.salesPersonId}
                                                    value={v.salesPersonId}
                                                    data-name={v.salesPerson}
                                                >
                                                    {v.salesPerson}
                                                </option>
                                            ))}
                                        </select>
                                        {errors.SalesPersonID && <p className="text-red-500 text-xs mt-1">{errors.SalesPersonID}</p>}
                                    </div>
                                    <div>
                                        <label className={labelClass}>Carrier Booking Number</label>
                                        <input type="text" name="CarrierBookingNo" value={formData?.CarrierBookingNo} onChange={handleInputChange} className={inputClass} placeholder="Enter carrier booking #" />
                                    </div>
                                    <div>
                                        <label className={labelClass}>Co-Loader Ref. Number</label>
                                        <input type="text" name="CoLoaderRefNumber" value={formData?.CoLoaderRefNumber} onChange={handleInputChange} className={inputClass} placeholder="Co-loader reference" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                                            Booking Type:
                                        </label>
                                        <select className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-1 focus:ring-[#0072FF]"
                                            value={formData?.BookingType || ''}
                                            onChange={(e) => {
                                                handleInputChange(e);
                                            }}
                                            name="BookingType"
                                        >   <option value="">Select Booking Type</option>
                                            <option value="Freight Forwarder">Freight Forwarder</option>
                                            <option value="NVO">NVO</option>
                                            <option value="Document Only">Document Only</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className={labelClass}>Agent Reference Number</label>
                                        <input type="text" name="Agent_RefNo" value={formData?.Agent_RefNo} onChange={handleInputChange} className={inputClass} placeholder="Agent ref #" />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-3 pt-2">
                                    <div>
                                        <label className={labelClass}>Entry Date</label>
                                        <DateInputwithcal onError={handleDateError} label="Entry Date" name="EntryDate" value={formData?.EntryDate ? new Date(formData?.EntryDate) : null} onChange={handleDateChange} required />
                                    </div>
                                    <div>
                                        <label className={labelClass}>Carrier Booking Confirm Date</label>
                                        <DateInputwithcal onError={handleDateError} label="Carrier Booking Confirm Date" name="CarrierBookingConfirmationDate" value={formData?.CarrierBookingConfirmationDate} onChange={handleDateChange} required />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-slate-100 dark:border-slate-800">
                                    <div>
                                        <label className={labelClass}>Overseas Agent</label>
                                        <SearchBar
                                            searchAPI={'v1/AutoSuggestion/GetVendorSuggestion?Prefix='}
                                            selectReacord={(d) => {
                                                handleInputChange({ target: { name: 'OverSeasAgent', value: d?.[1] } });
                                                handleInputChange({ target: { name: 'OverseasAgentID', value: d?.[0] } });
                                            }}
                                            value={formData?.OverSeasAgent || ''}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                                            Rate Quote Number:
                                        </label>

                                        <div className="relative">
                                            <Input
                                                className="w-full pr-10 px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-1 focus:ring-[#0072FF]"
                                                type="text"
                                                value={formData?.RateQuoteNumber || ""}
                                                onChange={handleInputChange}
                                                name="RateQuoteNumber"
                                            />

                                            {/* 👁 View Icon */}
                                            {formData?.RateQuoteID > 0 &&
                                                formData?.RateQuoteNumber &&
                                                formData?.RateQuoteNumber.trim() !== "" &&
                                                formData?.RateQuoteNumber !== "0" && (
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            window.open(
                                                                `/rate-quote/edit/${formData.RateQuoteID}`,
                                                                "_blank"
                                                            );
                                                        }}
                                                        className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-[#0072FF] transition"
                                                        title="View Rate Quote"
                                                    >
                                                        <Eye size={18} />
                                                    </button>
                                                )}
                                        </div>
                                    </div>

                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Shipper & Consignee Information */}
                <div ref={sectionRefs.shipperInfo} className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-700/30 dark:border-slate-700 overflow-hidden hover:shadow-md dark:shadow-none dark:hover:shadow-[0_0_12px_rgba(59,130,246,0.25)] transition-shadow duration-200">
                    <SectionHeader icon={Building2} title="Shipper & Consignee" color="green" section="shipperInfo" />
                    {expandedSections.shipperInfo && (
                        <div className="p-4 border-t border-slate-200 dark:border-slate-800">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                {/* LEFT COLUMN */}
                                <div className="space-y-6">
                                    {/* Shipper Card */}
                                    <div className="p-4 bg-blue-50/50 dark:bg-blue-950/20 rounded-xl border-l-2 border-l-blue-500 border border-blue-100 dark:border-blue-900/50 shadow-sm hover:shadow-md transition-shadow">
                                        <div className="flex items-center gap-2 mb-3">
                                            <label className="font-semibold text-blue-700 dark:text-blue-400">Shipper / Exporter <span className="text-red-500">*</span></label>
                                        </div>
                                        <SearchBar
                                            searchAPI={'v1/AutoSuggestion/GetConsigneeSuggestion?Prefix='}
                                            selectReacord={(d) => {
                                                handleInputChange({ target: { name: 'Shipper', value: d?.[1] } });
                                                handleInputChange({ target: { name: 'ShipperID', value: d?.[0] } });
                                                _setAddress('ShipperAddress', d?.[0]);
                                            }}
                                            value={formData?.Shipper || ''}
                                            onchange={(val) => {
                                                if (!val) {
                                                    handleInputChange({ target: { name: 'Shipper', value: '' } });
                                                    handleInputChange({ target: { name: 'ShipperID', value: 0 } });
                                                }
                                            }}
                                        />
                                        {errors.Shipper && <p className="text-red-500 text-xs mt-1">{errors.Shipper}</p>}
                                        <textarea name="ShipperAddress" value={formData?.ShipperAddress} onChange={handleInputChange} rows={6} className={`${inputClass} mt-2`} placeholder="Address (include phone if needed)" />
                                    </div>

                                    {/* Consignee Card */}
                                    <div className="p-4 bg-purple-50/50 dark:bg-purple-950/20 rounded-xl border-l-2 border-l-purple-500 border border-purple-100 dark:border-purple-900/50 shadow-sm hover:shadow-md transition-shadow">
                                        <div className="flex justify-between items-center mb-3">
                                            <div className="flex items-center gap-2">
                                                <label className="font-semibold text-purple-700 dark:text-purple-400">Consignee <span className="text-red-500">*</span></label>
                                            </div>
                                            <div className="flex items-center gap-2 bg-white dark:bg-slate-800 px-2 py-1 rounded-md shadow-sm">
                                                <span className="text-xs text-red-500 font-medium">Complexity Level</span>
                                                <select name="ComplexityLevel" value={formData?.ComplexityLevel} onChange={handleInputChange} className="bg-white dark:bg-slate-900 text-xs px-2 py-1 border border-slate-300 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 w-17">
                                                    <option value="--NA--">--NA--</option>
                                                    <option value="1">1</option>
                                                    <option value="2">2</option>
                                                    <option value="3">3</option>
                                                    <option value="4">4</option>
                                                    <option value="5">5</option>
                                                </select>
                                            </div>
                                        </div>
                                        <SearchBar
                                            searchAPI={'v1/AutoSuggestion/GetConsigneeSuggestion?Prefix='}
                                            selectReacord={(d) => {
                                                handleInputChange({ target: { name: 'Consignee', value: d?.[1] } });
                                                handleInputChange({ target: { name: 'ConsigneeID', value: d?.[0] } });
                                                _setAddress('ConsigneeAddress', d?.[0]);
                                            }}
                                            value={formData?.Consignee || ''}
                                            onchange={(val) => {
                                                if (!val) {
                                                    handleInputChange({ target: { name: 'Consignee', value: '' } });
                                                    handleInputChange({ target: { name: 'ConsigneeID', value: 0 } });
                                                }
                                            }}
                                        />
                                        {errors.Consignee && <p className="text-red-500 text-xs mt-1">{errors.Consignee}</p>}
                                        <textarea name="ConsigneeAddress" value={formData?.ConsigneeAddress} onChange={handleInputChange} rows={6} className={`${inputClass} mt-2`} placeholder="Address" />
                                    </div>

                                    {/* Notify Card */}
                                    <div className="p-4 bg-indigo-50/50 dark:bg-indigo-950/20 rounded-xl border-l-2 border-l-indigo-500 border border-indigo-100 dark:border-indigo-900/50 shadow-sm hover:shadow-md transition-shadow">
                                        <div className="flex justify-between items-center mb-3">
                                            <div className="flex items-center gap-2">
                                                <label className="font-semibold text-indigo-700 dark:text-indigo-400">Notify <span className="text-red-500">*</span></label>
                                            </div>
                                            <label className="text-xs flex items-center gap-1.5 text-slate-600 dark:text-slate-400 bg-white dark:bg-slate-800 px-2 py-1 rounded-md">
                                                <input
                                                    type="checkbox"
                                                    name="NotifySameAsConsignee"
                                                    checked={formData?.NotifySameAsConsignee}
                                                    onChange={() =>
                                                        handleCopyFields("NotifySameAsConsignee", "Consignee", [
                                                            { name: "Notify", valueKey: "Consignee" },
                                                            { name: "NotifyID", valueKey: "ConsigneeID", clearValue: 0 },
                                                            { name: "NotifyAddress", valueKey: "ConsigneeAddress" }
                                                        ])
                                                    }
                                                    className="rounded text-indigo-600 focus:ring-indigo-500"
                                                />
                                                Same as Consignee
                                            </label>
                                        </div>
                                        <SearchBar
                                            searchAPI={'v1/AutoSuggestion/GetConsigneeSuggestion?Prefix='}
                                            selectReacord={(d) => {
                                                handleInputChange({ target: { name: 'Notify', value: d?.[1] } });
                                                handleInputChange({ target: { name: 'NotifyID', value: d?.[0] } });
                                                _setAddress('NotifyAddress', d?.[0]);
                                            }}
                                            value={formData?.Notify || ''}
                                            onchange={(val) => {
                                                if (!val) {
                                                    handleInputChange({ target: { name: 'Notify', value: '' } });
                                                    handleInputChange({ target: { name: 'NotifyID', value: 0 } });
                                                }
                                            }}
                                        />
                                        {errors.Notify && <p className="text-red-500 text-xs mt-1">{errors.Notify}</p>}
                                        <textarea name="NotifyAddress" value={formData?.NotifyAddress} onChange={handleInputChange} rows={6} className={`${inputClass} mt-2`} placeholder="Address" />
                                    </div>

                                    {/* Also Notify Card */}
                                    <div className="p-4 bg-violet-50/50 dark:bg-violet-950/20 rounded-xl border-l-2 border-l-violet-500 border border-violet-100 dark:border-violet-900/50 shadow-sm hover:shadow-md transition-shadow">
                                        <div className="flex justify-between items-center mb-3">
                                            <div className="flex items-center gap-2">
                                                <label className="font-semibold text-violet-700 dark:text-violet-400">Also Notify <span className="text-red-500">*</span></label>
                                            </div>
                                            <label className="text-xs flex items-center gap-1.5 text-slate-600 dark:text-slate-400 bg-white dark:bg-slate-800 px-2 py-1 rounded-md">
                                                <input
                                                    type="checkbox"
                                                    name="AlsoNotifySameAsConsignee"
                                                    checked={formData?.AlsoNotifySameAsConsignee}
                                                    onChange={() =>
                                                        handleCopyFields("AlsoNotifySameAsConsignee", "Consignee", [
                                                            { name: "AlsoNotify", valueKey: "Consignee" },
                                                            { name: "AlsoNotifyID", valueKey: "ConsigneeID", clearValue: 0 },
                                                            { name: "AlsoNotifyAddress", valueKey: "ConsigneeAddress" }
                                                        ])
                                                    }
                                                    className="rounded text-violet-600 focus:ring-violet-500"
                                                />
                                                Same as Consignee
                                            </label>
                                        </div>
                                        <SearchBar
                                            searchAPI={'v1/AutoSuggestion/GetConsigneeSuggestion?Prefix='}
                                            selectReacord={(d) => {
                                                handleInputChange({ target: { name: 'AlsoNotify', value: d?.[1] } });
                                                handleInputChange({ target: { name: 'AlsoNotifyID', value: d?.[0] } });
                                                _setAddress('AlsoNotifyAddress', d?.[0]);
                                            }}
                                            value={formData?.AlsoNotify || ''}
                                            onchange={(val) => {
                                                if (!val) {
                                                    handleInputChange({ target: { name: 'AlsoNotify', value: '' } });
                                                    handleInputChange({ target: { name: 'AlsoNotifyID', value: 0 } });
                                                }
                                            }}
                                        />
                                        {errors.AlsoNotify && <p className="text-red-500 text-xs mt-1">{errors.AlsoNotify}</p>}
                                        <textarea name="AlsoNotifyAddress" value={formData?.AlsoNotifyAddress} onChange={handleInputChange} rows={6} className={`${inputClass} mt-2`} placeholder="Address" />
                                    </div>

                                    {/* Trucker Card */}
                                    <div className="p-4 bg-amber-50/50 dark:bg-amber-950/20 rounded-xl border-l-2 border-l-amber-500 border border-amber-100 dark:border-amber-900/50 shadow-sm hover:shadow-md transition-shadow">
                                        <div className="flex items-center gap-2 mb-3">
                                            <label className="font-semibold text-amber-700 dark:text-amber-400">Trucker <span className="text-red-500">*</span></label>
                                        </div>
                                        <SearchBar
                                            searchAPI={'v1/AutoSuggestion/GetTruckerAndTowing?Prefix='}
                                            selectReacord={(d) => {
                                                handleInputChange({ target: { name: 'TruckerName', value: d?.[1] } })
                                                handleInputChange({ target: { name: 'Trucker_id', value: d?.[0] } })
                                                _setAddress('TruckerAddress', d?.[0]);
                                            }}
                                            value={formData?.TruckerName}
                                            onchange={(val) => {
                                                if (!val) {
                                                    handleInputChange({ target: { name: 'TruckerName', value: '' } });
                                                    handleInputChange({ target: { name: 'Trucker_id', value: 0 } })
                                                }
                                            }}
                                        />
                                        {errors.TruckerName && <p className="text-red-500 text-xs mt-1">{errors.TruckerName}</p>}
                                        <textarea name="TruckerAddress" value={formData?.TruckerAddress} onChange={handleInputChange} rows={6} className={`${inputClass} mt-2`} placeholder="Trucker Details (address, phone)" />
                                    </div>

                                    {/* Broker Card */}
                                    <div className="p-4 bg-orange-50/50 dark:bg-orange-950/20 rounded-xl border-l-2 border-l-orange-500 border border-orange-100 dark:border-orange-900/50 shadow-sm hover:shadow-md transition-shadow">
                                        <div className="flex items-center gap-2 mb-3">
                                            <label className="font-semibold text-orange-700 dark:text-orange-400">Broker <span className="text-red-500">*</span></label>
                                        </div>
                                        <SearchBar
                                            searchAPI={'v1/AutoSuggestion/GetTruckerAndTowing?Prefix='}
                                            selectReacord={(d) => {
                                                handleInputChange({ target: { name: 'BrokerName', value: d?.[1] } })
                                                handleInputChange({ target: { name: 'BrokerID', value: d?.[0] } })
                                                _setAddress('BrokerAddress', d?.[0]);
                                            }}
                                            value={formData?.BrokerName}
                                            onchange={(val) => {
                                                if (!val) {
                                                    handleInputChange({ target: { name: 'BrokerName', value: '' } });
                                                    handleInputChange({ target: { name: 'BrokerID', value: 0 } })
                                                }
                                            }}
                                        />
                                        {errors.BrokerName && <p className="text-red-500 text-xs mt-1">{errors.BrokerName}</p>}
                                        <textarea name="BrokerAddress" value={formData?.BrokerAddress} onChange={handleInputChange} rows={6} className={`${inputClass} mt-2`} placeholder="Address" />
                                    </div>
                                </div>

                                {/* RIGHT COLUMN */}
                                <div className="space-y-6">
                                    {/* Export Ref */}
                                    <div className="p-4 bg-cyan-50/50 dark:bg-cyan-950/20 rounded-xl border-l-2 border-l-cyan-500 border border-cyan-100 dark:border-cyan-900/50 shadow-sm hover:shadow-md transition-shadow">
                                        <div className="flex items-center gap-2 mb-3">
                                            <label className="font-semibold text-cyan-700 dark:text-cyan-400">Export Ref. Number</label>
                                        </div>
                                        <input type="text" name="ExportRefNumber" value={formData?.ExportRefNumber} onChange={handleInputChange} className={inputClass} />
                                    </div>

                                    {/* Forwarding Agent */}
                                    <div className="p-4 bg-sky-50/50 dark:bg-sky-950/20 rounded-xl border-l-2 border-l-sky-500 border border-sky-100 dark:border-sky-900/50 shadow-sm hover:shadow-md transition-shadow">
                                        <div className="flex items-center gap-2 mb-3">
                                            <label className="font-semibold text-sky-700 dark:text-sky-400">Forwarding Agent</label>
                                        </div>
                                        <SearchBar
                                            searchAPI={'v1/AutoSuggestion/GetConsigneeSuggestion?Prefix='}
                                            selectReacord={(d) => {
                                                handleInputChange({ target: { name: 'ForwardingAgent', value: d?.[1] } });
                                                handleInputChange({ target: { name: 'ForwardingAgentID', value: d?.[0] } });
                                                _setAddress('ForwardingAgentAddress', d?.[0]);
                                            }}
                                            onchange={(val) => {
                                                if (!val) {
                                                    handleInputChange({ target: { name: 'ForwardingAgent', value: '' } });
                                                    handleInputChange({ target: { name: 'ForwardingAgentID', value: 0 } });
                                                }
                                            }}
                                            value={formData?.ForwardingAgent || ''}
                                        />
                                        <textarea name="ForwardingAgentAddress" value={formData?.ForwardingAgentAddress} onChange={handleInputChange} rows={6} className={`${inputClass} mt-2`} placeholder="Address (include phone if needed)" />
                                    </div>

                                    {/* FMC License */}
                                    <div className="p-4 bg-teal-50/50 dark:bg-teal-950/20 rounded-xl border-l-2 border-l-teal-500 border border-teal-100 dark:border-teal-900/50 shadow-sm hover:shadow-md transition-shadow">
                                        <div className="flex items-center gap-2 mb-3">
                                            <label className="font-semibold text-teal-700 dark:text-teal-400">FMC Licence Number</label>
                                        </div>
                                        <input type="text" name="FmcNo" value={formData?.FmcNo} onChange={handleInputChange} className={inputClass} />
                                    </div>

                                    {/* Ultimate Consignee */}
                                    <div className="p-4 bg-pink-50/50 dark:bg-pink-950/20 rounded-xl border-l-2 border-l-pink-500 border border-pink-100 dark:border-pink-900/50 shadow-sm hover:shadow-md transition-shadow">
                                        <div className="flex justify-between items-center mb-3">
                                            <div className="flex items-center gap-2">
                                                <label className="font-semibold text-pink-700 dark:text-pink-400">Ultimate Consignee</label>
                                            </div>
                                            <label className="text-xs flex items-center gap-1.5 text-slate-600 dark:text-slate-400 bg-white dark:bg-slate-800 px-2 py-1 rounded-md">
                                                <input
                                                    type="checkbox"
                                                    name="UltimateConsigneeSameAsConsignee"
                                                    checked={formData?.UltimateConsigneeSameAsConsignee}
                                                    onChange={() =>
                                                        handleCopyFields("UltimateConsigneeSameAsConsignee", "Consignee", [
                                                            { name: "UltimateConsignee", valueKey: "Consignee" },
                                                            { name: "UltimateConsigneeID", valueKey: "ConsigneeID", clearValue: 0 },
                                                            { name: "UltimateConsigneeAddress", valueKey: "ConsigneeAddress" }
                                                        ])
                                                    }
                                                    className="rounded text-pink-600 focus:ring-pink-500"
                                                />
                                                Same as Consignee
                                            </label>
                                        </div>
                                        <SearchBar
                                            searchAPI={'v1/AutoSuggestion/GetConsigneeSuggestion?Prefix='}
                                            selectReacord={(d) => {
                                                handleInputChange({ target: { name: 'UltimateConsignee', value: d?.[1] } });
                                                handleInputChange({ target: { name: 'UltimateConsigneeID', value: d?.[0] } });
                                                _setAddress('UltimateConsigneeAddress', d?.[0]);
                                            }}
                                            onchange={(val) => {
                                                if (!val) {
                                                    handleInputChange({ target: { name: 'UltimateConsignee', value: '' } });
                                                    handleInputChange({ target: { name: 'UltimateConsigneeID', value: 0 } });
                                                }
                                            }}
                                            value={formData?.UltimateConsignee || ''}
                                        />
                                        <textarea name="UltimateConsigneeAddress" value={formData?.UltimateConsigneeAddress} onChange={handleInputChange} rows={6} className={`${inputClass} mt-2`} placeholder="Address" />
                                    </div>

                                    {/* Delivery Agent */}
                                    <div className="p-4 bg-rose-50/50 dark:bg-rose-950/20 rounded-xl border-l-2 border-l-rose-500 border border-rose-100 dark:border-rose-900/50 shadow-sm hover:shadow-md transition-shadow">
                                        <div className="flex items-center gap-2 mb-3">
                                            <label className="font-semibold text-rose-700 dark:text-rose-400">For Delivery Please Contact <span className="text-red-500">*</span></label>
                                        </div>
                                        <SearchBar
                                            searchAPI={'v1/AutoSuggestion/GetConsigneeSuggestion?Prefix='}
                                            selectReacord={(d) => {
                                                handleInputChange({ target: { name: 'DeliveryAgent', value: d?.[1] } });
                                                handleInputChange({ target: { name: 'DeliveryID', value: d?.[0] } });
                                                _setAddress('DeliveryAddress', d?.[0]);
                                            }}
                                            onchange={(val) => {
                                                if (!val) {
                                                    handleInputChange({ target: { name: 'DeliveryAgent', value: '' } });
                                                    handleInputChange({ target: { name: 'DeliveryID', value: 0 } });
                                                }
                                            }}
                                            value={formData?.DeliveryAgent || ''}
                                        />
                                        {errors.DeliveryAgent && <p className="text-red-500 text-xs mt-1">{errors.DeliveryAgent}</p>}
                                        <textarea name="DeliveryAddress" value={formData?.DeliveryAddress} onChange={handleInputChange} rows={6} className={`${inputClass} mt-2`} placeholder="Address (include phone)" />
                                    </div>

                                    {/* Bill To Party */}
                                    <div className="p-4 bg-red-50/50 dark:bg-red-950/20 rounded-xl border-l-2 border-l-red-500 border border-red-100 dark:border-red-900/50 shadow-sm hover:shadow-md transition-shadow">
                                        <div className="flex items-center gap-2 mb-3">
                                            <label className="font-semibold text-red-700 dark:text-red-400">Bill To Party <span className="text-red-500">*</span></label>
                                        </div>
                                        <SearchBar
                                            searchAPI={'v1/AutoSuggestion/GetConsigneeSuggestion?Prefix='}
                                            selectReacord={(d) => {
                                                handleInputChange({ target: { name: 'BillToParty', value: d?.[1] } });
                                                handleInputChange({ target: { name: 'BillToID', value: d?.[0] } });
                                                _setAddress('BillToPatyAddress', d?.[0]);
                                            }}
                                            onchange={(val) => {
                                                if (!val) {
                                                    handleInputChange({ target: { name: 'BillToParty', value: '' } });
                                                    handleInputChange({ target: { name: 'BillToID', value: 0 } });
                                                }
                                            }}
                                            value={formData?.BillToParty || ''}
                                        />
                                        {errors.BillToParty && <p className="text-red-500 text-xs mt-1">{errors.BillToParty}</p>}
                                        <textarea name="BillToPatyAddress" value={formData?.BillToPatyAddress} onChange={handleInputChange} rows={6} className={`${inputClass} mt-2`} placeholder="Address (include phone, email)" />
                                    </div>

                                    {/* Shipment Type */}
                                    <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm">
                                        <div className="flex items-center gap-2 mb-3">
                                            <label className="font-semibold text-slate-700 dark:text-slate-300">Shipment Type</label>
                                        </div>
                                        <div className="flex flex-wrap gap-4 mt-2">
                                            {[
                                                { label: "Agent Routed", key: "AgentRouted" },
                                                { label: "Agent Delivery", key: "AgentDelivery" },
                                                { label: "Auto Shipment", key: "AutoShipment" },
                                                { label: "U.S Flag Cargo", key: "USFlagCargo" },
                                                { label: "Free Shipment", key: "FreeShipment" },
                                                { label: "RFP", key: "RFP" },
                                                { label: "Pool Shipment", key: "PoolShipment" },
                                                { label: "MARAD", key: "MARAD" },
                                            ].map((item) => (
                                                <label
                                                    key={item.key}
                                                    className="text-sm flex items-center gap-2 text-slate-700 dark:text-slate-300 cursor-pointer hover:text-slate-900 dark:hover:text-slate-100 transition-colors"
                                                >
                                                    <input
                                                        type="checkbox"
                                                        name={item.key}
                                                        checked={formData?.[item.key] || false}
                                                        onChange={(e) =>
                                                            setFormData((prev) => ({
                                                                ...prev,
                                                                [item.key]: e.target.checked,
                                                            }))
                                                        }
                                                        className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                                                    />
                                                    {item.label}
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Routing Information */}
                <div ref={sectionRefs.routingInfo} className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-700/30 dark:border-slate-700 overflow-hidden hover:shadow-md dark:shadow-none dark:hover:shadow-[0_0_12px_rgba(59,130,246,0.25)] transition-shadow duration-200">
                    <SectionHeader icon={Route} title="Routing Information" color="cyan" section="routingInfo" />
                    {expandedSections.routingInfo && (
                        <div className="p-3 border-t border-slate-200 dark:border-slate-800">
                            <RoutingInformation
                                formData={routingFormSlice}
                                onChange={handleInputChange}
                                inputClass={inputClass}
                                selectClass={selectClass}
                                labelClass={labelClass}
                                handleDateChange={handleDateChange}
                                handleDateError={handleDateError}
                                requiredFields={ROUTING_SECTION_REQUIRED_FIELDS}
                                countryList={countryList}
                                moveTypeList={moveTypeList}
                                coloaderList={coloaderList}
                                errors={errors}
                                setAddress={_setAddress}
                            />
                        </div>
                    )}
                </div>

                {/* Parties Information */}
                <div ref={sectionRefs.partiesInfo} className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-700/30 dark:border-slate-700 overflow-hidden hover:shadow-md dark:shadow-none dark:hover:shadow-[0_0_12px_rgba(59,130,246,0.25)] transition-shadow duration-200">
                    <SectionHeader icon={Building2} title="Parties Information" color="indigo" section="partiesInfo" />
                    {expandedSections.partiesInfo && (
                        <div className="p-3 border-t border-slate-200 dark:border-slate-800">
                            <PartiesInformation
                                formData={partiesFormSlice}
                                handleChange={handleInputChange}
                                handleCopyFields={handleCopyFields}
                                inputClass={inputClass}
                                labelClass={labelClass}
                                setAddress={_setAddress}
                            />
                        </div>
                    )}
                </div>

                {/* Cargo Details (LCL/RO-RO) */}
                <div ref={sectionRefs.cargoDetails} className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-700/30 dark:border-slate-700 overflow-hidden mt-2 hover:shadow-md dark:shadow-none dark:hover:shadow-[0_0_12px_rgba(59,130,246,0.25)] transition-shadow duration-200">
                    <SectionHeader icon={Ship} title="Cargo Details (LCL/RO-RO)" color="cyan" section="cargoDetails" />
                    {expandedSections.cargoDetails && (
                        <div className="p-5 space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                <div className="space-y-2">
                                    <label className={labelClass}>Piece</label>
                                    <Input className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-1 focus:ring-[#0072FF]" type="number" value={formData?.totalPackageType || ''} onChange={(e) => handleInputChange({ target: { name: 'totalPackageType', value: e.target.value } })} placeholder="0" min="0" onKeyPress={(e) => { if (!/[0-9]/.test(e.key)) e.preventDefault(); }} />
                                </div>
                                <div className="space-y-2">
                                    <label className={labelClass}>Package Type</label>
                                    <select className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 rounded-md text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#0072FF] focus:border-transparent" value={formData?.packageType || ''} onChange={(e) => handleInputChange({ target: { name: 'packageType', value: e.target.value } })}>
                                        <option value="">Select type</option>
                                        {/* <option value="carton">Carton</option>
                                        <option value="pallet">Pallet</option>
                                        <option value="crate">Crate</option>
                                        <option value="drum">Drum</option>
                                        <option value="bundle">Bundle</option>
                                        <option value="bag">Bag</option>
                                        <option value="box">Box</option>
                                        <option value="piece">Piece</option> */}
                                        {
                                            packageTypeList?.map((item, index) => (
                                                <option key={index} value={item.package}>{item.package}</option>
                                            ))
                                        }
                                    </select>
                                </div>
                                <div className="space-y-2 hidden">
                                    <label className={labelClass}>Description</label>
                                    <textarea className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 rounded-md text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#0072FF] focus:border-transparent" rows="2" value={formData?.cargoDescription || ''} onChange={(e) => handleInputChange({ target: { name: 'cargoDescription', value: e.target.value } })} placeholder="Cargo description" />
                                </div>
                            </div>

                            <div className="flex items-center space-x-2">
                                <input className="h-4 w-4 rounded border-slate-300 dark:border-slate-700 text-[#0072FF] focus:ring-[#0072FF]" type="checkbox" checked={formData?.autoCalculate !== false} onChange={(e) => handleInputChange({ target: { name: 'autoCalculate', value: e.target.checked } })} />
                                <label className="text-sm font-medium text-slate-600 dark:text-slate-400">Auto Calculate CBM, CFT & LBS</label>
                            </div>

                            <div className="overflow-x-auto border border-slate-200 dark:border-slate-700 rounded-lg">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
                                            <th className="text-left py-3 px-3 font-medium text-slate-600 dark:text-slate-400">Piece</th>
                                            <th className="text-left py-3 px-3 font-medium text-slate-600 dark:text-slate-400">Length</th>
                                            <th className="text-left py-3 px-3 font-medium text-slate-600 dark:text-slate-400">Width</th>
                                            <th className="text-left py-3 px-3 font-medium text-slate-600 dark:text-slate-400">Height</th>
                                            <th className="text-left py-3 px-3 font-medium text-slate-600 dark:text-slate-400">Package Type</th>
                                            <th className="text-left py-3 px-3 font-medium text-slate-600 dark:text-slate-400">Type</th>
                                            <th className="text-left py-3 px-3 font-medium text-slate-600 dark:text-slate-400">CBM</th>
                                            <th className="text-left py-3 px-3 font-medium text-slate-600 dark:text-slate-400">CFT</th>
                                            <th className="text-left py-3 px-3 font-medium text-slate-600 dark:text-slate-400">Wt (KGS)</th>
                                            <th className="text-left py-3 px-3 font-medium text-slate-600 dark:text-slate-400">Wt (LBS)</th>
                                            <th className="text-left py-3 px-3 font-medium text-slate-600 dark:text-slate-400"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {cargoItems.filter(item => !item.ISDELETED).map((item, index) => (
                                            <tr key={index} className="border-b border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/30">
                                                <td className="py-2 px-3"><Input className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-1 focus:ring-[#0072FF]" type="number" value={item.PIECE || ''} onChange={(e) => handleCargoItemChange(index, 'PIECE', e.target.value)} min="0" placeholder="0" /></td>
                                                <td className="py-2 px-3"><Input className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-1 focus:ring-[#0072FF]" type="number" value={item.LENGTH || ''} onChange={(e) => handleCargoItemChange(index, 'LENGTH', e.target.value)} min="0" step="0.01" placeholder="0.00" /></td>
                                                <td className="py-2 px-3"><Input className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-1 focus:ring-[#0072FF]" type="number" value={item.WIDTH || ''} onChange={(e) => handleCargoItemChange(index, 'WIDTH', e.target.value)} min="0" step="0.01" placeholder="0.00" /></td>
                                                <td className="py-2 px-3"><Input className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-1 focus:ring-[#0072FF]" type="number" value={item.HEIGHT || ''} onChange={(e) => handleCargoItemChange(index, 'HEIGHT', e.target.value)} min="0" step="0.01" placeholder="0.00" /></td>
                                                <td className="py-2 px-3">
                                                    <select className="w-30 px-2 py-1 border border-slate-300 dark:border-slate-700 rounded text-sm bg-white dark:bg-slate-800 focus:outline-none focus:ring-1 focus:ring-[#0072FF]" value={item.PACKAGE_TYPE || ''} onChange={(e) => handleCargoItemChange(index, 'PACKAGE_TYPE', e.target.value)}>
                                                        <option value="">Select type</option>
                                                        {/* <option value="carton">Carton</option>
                                                        <option value="pallet">Pallet</option>
                                                        <option value="crate">Crate</option>
                                                        <option value="drum">Drum</option>
                                                        <option value="bundle">Bundle</option>
                                                        <option value="bag">Bag</option>
                                                        <option value="box">Box</option>
                                                        <option value="piece">Piece</option> */}
                                                        {
                                                            packageTypeList?.map((item, index) => (
                                                                <option key={index} value={item.package}>{item.package}</option>
                                                            ))
                                                        }
                                                    </select>
                                                </td>
                                                <td className="py-2 px-3">
                                                    <select className="w-24 px-2 py-1 border border-slate-300 dark:border-slate-700 rounded text-sm bg-white dark:bg-slate-800 focus:outline-none focus:ring-1 focus:ring-[#0072FF]" value={item.DIMENSION_TYPE || 'METER'} onChange={(e) => handleCargoItemChange(index, 'DIMENSION_TYPE', e.target.value)}>
                                                        <option value="INCH">INCH</option>
                                                        <option value="CM">CM</option>
                                                        <option value="METER">METER</option>
                                                    </select>
                                                </td>
                                                <td className="py-2 px-3"><Input className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-1 focus:ring-[#0072FF]" type="number" value={item.CBM || ''} readOnly placeholder="0.00" /></td>
                                                <td className="py-2 px-3"><Input className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-1 focus:ring-[#0072FF]" type="number" value={item.CFT || ''} readOnly placeholder="0.00" /></td>
                                                <td className="py-2 px-3"><Input className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-1 focus:ring-[#0072FF]" type="number" value={item.WEIGHT_KGS || ''} onChange={(e) => handleCargoItemChange(index, 'WEIGHT_KGS', e.target.value)} min="0" step="0.01" placeholder="0.00" /></td>
                                                <td className="py-2 px-3"><Input className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-1 focus:ring-[#0072FF]" type="number" value={item.WEIGHT_LBS || ''} readOnly placeholder="0.00" /></td>
                                                <td className="py-2 px-3">
                                                    <button type="button" onClick={() => removeCargoRow(index)} className="inline-flex items-center justify-center h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors" disabled={cargoItems.length === 1}>
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <button type="button" onClick={addCargoRow} className="px-3 py-1 bg-green-600 text-white rounded">Add More Row</button>

                            <div className="overflow-x-auto border border-slate-200 dark:border-slate-700 rounded-lg">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
                                            <th className="text-center py-3 px-4 font-medium text-slate-600 dark:text-slate-400">Total Pieces</th>
                                            <th className="text-center py-3 px-4 font-medium text-slate-600 dark:text-slate-400">Total Wt. LBS</th>
                                            <th className="text-center py-3 px-4 font-medium text-slate-600 dark:text-slate-400">Total Wt. KGS</th>
                                            <th className="text-center py-3 px-4 font-medium text-slate-600 dark:text-slate-400">Total Volume - CFT</th>
                                            <th className="text-center py-3 px-4 font-medium text-slate-600 dark:text-slate-400">Total Volume - CBM</th>
                                            <th className="text-center py-3 px-4 font-medium text-slate-600 dark:text-slate-400">Density (KG/CBM)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr className="bg-slate-50 dark:bg-slate-800/30">
                                            <td className="py-3 px-4 text-center"><div className="inline-block bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded px-4 py-1.5 font-medium">{finalTotals.totalPieces}</div></td>
                                            <td className="py-3 px-4 text-center"><div className="inline-block bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded px-4 py-1.5 font-medium">{finalTotals.totalWeightLbs}</div></td>
                                            <td className="py-3 px-4 text-center"><div className="inline-block bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded px-4 py-1.5 font-medium">{finalTotals.totalWeightKgs}</div></td>
                                            <td className="py-3 px-4 text-center"><div className="inline-block bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded px-4 py-1.5 font-medium text-blue-600 dark:text-blue-300">{finalTotals.totalVolumeCft}</div></td>
                                            <td className="py-3 px-4 text-center"><div className="inline-block bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded px-4 py-1.5 font-medium text-blue-600 dark:text-blue-300">{finalTotals.totalVolumeCbm}</div></td>
                                            <td className="py-3 px-4 text-center"><div className="inline-block bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded px-4 py-1.5 font-medium">{finalTotals.density}</div></td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>

                {/* HBL Payment / Release Mode / Reefer Details - ADDED ref */}
                <div ref={sectionRefs.mode} className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-700/30 dark:border-slate-700 overflow-hidden hover:shadow-md dark:shadow-none dark:hover:shadow-[0_0_12px_rgba(59,130,246,0.25)] transition-shadow duration-200">
                    <SectionHeader icon={Package} title="HBL Payment / Release Mode / Reefer Details" color="orange" section="mode" />
                    {expandedSections.mode && (
                        <div className="p-4 border-t border-slate-200 dark:border-slate-800">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                {/* LEFT COLUMN */}
                                <div className="space-y-6">
                                    {/* HBL Payment Mode Card */}
                                    <div className="bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-950/20 dark:to-green-950/10 rounded-xl border-l-2 border-l-emerald-500 border border-slate-200 dark:border-slate-700 p-4 shadow-sm">
                                        <div className="flex items-center gap-2 mb-4">
                                            <div className="p-1.5 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
                                                <DollarSign className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                                            </div>
                                            <h3 className="font-semibold text-emerald-700 dark:text-emerald-400 text-sm">HBL Payment Mode</h3>
                                        </div>
                                        <div className="flex flex-wrap gap-6">
                                            {[
                                                { value: "P", label: "Prepaid", color: "emerald" },
                                                { value: "C", label: "Collect", color: "blue" },
                                                { value: "N", label: "No Status", color: "gray" }
                                            ].map((option) => (
                                                <label key={option.value} className="flex items-center gap-2 text-sm cursor-pointer group">
                                                    <input
                                                        type="radio"
                                                        name="HBLPaymentMode"
                                                        value={option.value}
                                                        checked={formData?.HBLPaymentMode === option.value}
                                                        onChange={() => handleInputChange({ target: { name: "HBLPaymentMode", value: option.value } })}
                                                        className={`w-4 h-4 accent-${option.color}-600`}
                                                    />
                                                    <span className="text-slate-700 dark:text-slate-300 group-hover:text-slate-900 dark:group-hover:text-white transition-colors">
                                                        {option.label}
                                                    </span>
                                                </label>
                                            ))}
                                        </div>
                                        <div className="mt-3">
                                            <label className={labelClass}>Payment Mode (Text):</label>
                                            <Input
                                                value={formData?.PaymentMode || ''}
                                                className={inputClass}
                                                onChange={(e) => handleInputChange({ target: { name: 'PaymentMode', value: e.target.value } })}
                                                placeholder="Enter payment mode description..."
                                            />
                                        </div>
                                    </div>

                                    {/* Carrier Payment Mode Card */}
                                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/10 rounded-xl border-l-2 border-l-blue-500 border border-slate-200 dark:border-slate-700 p-4 shadow-sm">
                                        <div className="flex items-center gap-2 mb-4">
                                            <div className="p-1.5 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                                                <Truck className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                            </div>
                                            <h3 className="font-semibold text-blue-700 dark:text-blue-400 text-sm">Carrier Payment Mode</h3>
                                        </div>
                                        <div className="flex flex-wrap gap-6">
                                            {[
                                                { value: "P", label: "Prepaid", carrierValue: "freight Prepaid" },
                                                { value: "C", label: "Collect", carrierValue: "freight Collect" },
                                                { value: "N", label: "No Status", carrierValue: "freight NoStatus" }
                                            ].map((option) => (
                                                <label key={option.value} className="flex items-center gap-2 text-sm cursor-pointer group">
                                                    <input
                                                        type="radio"
                                                        name="carrierPaymentRadio"
                                                        value={option.value}
                                                        checked={formData?.PaymentMode === option.value}
                                                        onChange={() => {
                                                            handleInputChange({
                                                                target: { name: "PaymentMode", value: option.value }
                                                            });
                                                            handleInputChange({
                                                                target: {
                                                                    name: "CarrierPayment",
                                                                    value: option.carrierValue
                                                                }
                                                            });
                                                        }}
                                                        className="w-4 h-4 accent-blue-600"
                                                    />
                                                    <span className="text-slate-700 dark:text-slate-300 group-hover:text-slate-900 dark:group-hover:text-white transition-colors">
                                                        {option.label}
                                                    </span>
                                                </label>
                                            ))}
                                        </div>
                                        <div className="mt-3">
                                            <label className={labelClass}>Carrier Payment Mode (Text):</label>
                                            <Input
                                                value={formData?.CarrierPayment || ''}
                                                className={inputClass}
                                                onChange={(e) => handleInputChange({ target: { name: 'CarrierPayment', value: e.target.value } })}
                                                placeholder="Enter carrier payment mode..."
                                            />
                                        </div>
                                    </div>

                                    {/* Business & Hazmat Card */}
                                    <div className="bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-950/20 dark:to-yellow-950/10 rounded-xl border-l-2 border-l-amber-500 border border-slate-200 dark:border-slate-700 p-4 shadow-sm">
                                        <div className="flex items-center gap-2 mb-4">
                                            <div className="p-1.5 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                                                <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                                            </div>
                                            <h3 className="font-semibold text-amber-700 dark:text-amber-400 text-sm">Business & Hazmat Details</h3>
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className={labelClass}>Business Days:</label>
                                                <Input
                                                    value={formData?.BusinessDays || ''}
                                                    className={inputClass}
                                                    onChange={(e) => handleInputChange({ target: { name: 'BusinessDays', value: e.target.value } })}
                                                    placeholder="Enter business days..."
                                                />
                                            </div>
                                            <div>
                                                <label className={labelClass}>Haz Cargo:</label>
                                                <div className="flex gap-4 mt-1">
                                                    {["YES", "NO"].map(val => (
                                                        <label key={val} className="flex items-center gap-1.5 text-sm cursor-pointer">
                                                            <input
                                                                type="radio"
                                                                name="HazCargo"
                                                                value={val}
                                                                checked={formData?.HazCargo === val}
                                                                onChange={() => handleInputChange({ target: { name: 'HazCargo', value: val } })}
                                                                className="accent-amber-600"
                                                            />
                                                            <span className="text-slate-700 dark:text-slate-300">{val}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-3 gap-3 mt-4">
                                            <div>
                                                <label className={labelClass}>IMO CLASS <span className="text-red-500">*</span></label>
                                                <input name="IMOClass" value={formData?.IMOClass || ''} onChange={handleInputChange} className={inputClass} placeholder="IMO Class" />
                                            </div>
                                            <div>
                                                <label className={labelClass}>PKG # <span className="text-red-500">*</span></label>
                                                <input name="Pkg" value={formData?.Pkg || ''} onChange={handleInputChange} className={inputClass} placeholder="Package #" />
                                            </div>
                                            <div>
                                                <label className={labelClass}>UN NBR <span className="text-red-500">*</span></label>
                                                <input name="UN_NBR" value={formData?.UN_NBR || ''} onChange={handleInputChange} className={inputClass} placeholder="UN Number" />
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-3 mt-2">
                                            <div>
                                                <label className={labelClass}>Gross Weight</label>
                                                <input name="GrossWeight" value={formData?.GrossWeight || ''} onChange={handleInputChange} className={inputClass} placeholder="Weight" />
                                            </div>
                                            <div>
                                                <label className={labelClass}>Unit</label>
                                                <select name="weightUnit" value={formData?.weightUnit || 'KGS'} onChange={handleInputChange} className={inputClass}>
                                                    <option value="KGS">KGS</option>
                                                    <option value="LBS">LBS</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Reefer Details Card */}
                                    <div className="bg-gradient-to-br from-cyan-50 to-teal-50 dark:from-cyan-950/20 dark:to-teal-950/10 rounded-xl border-l-2 border-l-cyan-500 border border-slate-200 dark:border-slate-700 p-4 shadow-sm">
                                        <div className="flex items-center gap-2 mb-4">
                                            <div className="p-1.5 bg-cyan-100 dark:bg-cyan-900/30 rounded-lg">
                                                <Thermometer className="w-4 h-4 text-cyan-600 dark:text-cyan-400" />
                                            </div>
                                            <h3 className="font-semibold text-cyan-700 dark:text-cyan-400 text-sm">Reefer Details</h3>
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className={labelClass}>Reefer Container (Temperature)</label>
                                                <input name="ReeferContainerTemp" value={formData?.ReeferContainerTemp || ''} onChange={handleInputChange} className={inputClass} placeholder="e.g., -20" />
                                            </div>
                                            <div>
                                                <label className={labelClass}>Unit of Measure</label>
                                                <select name="UnitOfMesasure" value={formData?.UnitOfMesasure || ''} onChange={handleInputChange} className={inputClass}>
                                                    <option value="">Select UOM</option>
                                                    <option value="C">°C (Celsius)</option>
                                                    <option value="F">°F (Fahrenheit)</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div className="mt-3">
                                            <label className={labelClass}>Vent Status:</label>
                                            <div className="flex gap-4 mt-1">
                                                {["OPEN", "CLOSED"].map(val => (
                                                    <label key={val} className="flex items-center gap-1.5 text-sm cursor-pointer">
                                                        <input
                                                            type="radio"
                                                            name="VentStatus"
                                                            value={val}
                                                            checked={formData?.VentStatus === val}
                                                            onChange={() => handleInputChange({ target: { name: 'VentStatus', value: val } })}
                                                            className="accent-cyan-600"
                                                        />
                                                        <span className="text-slate-700 dark:text-slate-300">{val}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="mt-2">
                                            <label className={labelClass}>Vent Remarks</label>
                                            <Input
                                                value={formData?.VentRemarks || ''}
                                                className={inputClass}
                                                onChange={(e) => handleInputChange({ target: { name: 'VentRemarks', value: e.target.value } })}
                                                placeholder="Enter vent remarks..."
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* RIGHT COLUMN */}
                                <div className="space-y-6">
                                    {/* Release Mode Card */}
                                    <div className="bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-950/20 dark:to-violet-950/10 rounded-xl border-l-2 border-l-purple-500 border border-slate-200 dark:border-slate-700 p-4 shadow-sm">
                                        <div className="flex items-center gap-2 mb-4">
                                            <div className="p-1.5 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                                                <FileCheck className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                                            </div>
                                            <h3 className="font-semibold text-purple-700 dark:text-purple-400 text-sm">Release Mode</h3>
                                        </div>
                                        <div className="space-y-3">
                                            <div className="flex flex-wrap gap-4">
                                                {[
                                                    { value: "N", label: "No Status", color: "gray" },
                                                    { value: "O", label: "Original", color: "blue" },
                                                    { value: "E", label: "Express", color: "green" },
                                                    { value: "R", label: "Release Originals at Destination", color: "purple" }
                                                ].map((option) => (
                                                    <label key={option.value} className="flex items-center gap-2 cursor-pointer group">
                                                        <input
                                                            type="radio"
                                                            name="HBLReleaseMode"
                                                            value={option.value}
                                                            checked={formData?.HBLReleaseMode === option.value}
                                                            onChange={() => handleInputChange({ target: { name: "HBLReleaseMode", value: option.value } })}
                                                            className={`w-4 h-4 accent-${option.color}-600`}
                                                        />
                                                        <span className="text-sm text-slate-700 dark:text-slate-300 group-hover:text-slate-900 dark:group-hover:text-white transition-colors">
                                                            {option.label}
                                                        </span>
                                                    </label>
                                                ))}
                                            </div>

                                            <div className="grid grid-cols-2 gap-3">
                                                <div>
                                                    <label className={labelClass}>Declared Value</label>
                                                    <input name="PrintDeclaredValue" value={formData?.PrintDeclaredValue || ''} onChange={handleInputChange} className={inputClass} placeholder="Declared value" />
                                                </div>
                                                <div>
                                                    <label className={labelClass}>No. Original (Print)</label>
                                                    <input name="NoOriginal" type="number" value={formData?.NoOriginal || 3} onChange={handleInputChange} className={inputClass} />
                                                </div>
                                            </div>

                                            <div>
                                                <label className="flex items-center gap-2 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={formData?.OnBoardPrint || false}
                                                        onChange={(e) => handleInputChange({ target: { name: 'OnBoardPrint', value: e.target.checked } })}
                                                        className="rounded accent-purple-600"
                                                    />
                                                    <span className="text-sm text-slate-700 dark:text-slate-300">On-Board (For Original)</span>
                                                </label>
                                            </div>

                                            <div>
                                                <label className={labelClass}>Comment</label>
                                                <textarea
                                                    className={`${inputClass} resize-none`}
                                                    rows={2}
                                                    value={formData?.ReleaseModeComment || ''}
                                                    onChange={(e) => handleInputChange({ target: { name: 'ReleaseModeComment', value: e.target.value } })}
                                                    placeholder="Enter release mode comments..."
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Clauses & Documents Card */}
                                    <div className="bg-gradient-to-br from-rose-50 to-pink-50 dark:from-rose-950/20 dark:to-pink-950/10 rounded-xl border-l-2 border-l-rose-500 border border-slate-200 dark:border-slate-700 p-4 shadow-sm">
                                        <div className="flex items-center gap-2 mb-4">
                                            <div className="p-1.5 bg-rose-100 dark:bg-rose-900/30 rounded-lg">
                                                <FileText className="w-4 h-4 text-rose-600 dark:text-rose-400" />
                                            </div>
                                            <h3 className="font-semibold text-rose-700 dark:text-rose-400 text-sm">Clauses & Documents</h3>
                                        </div>
                                        <div className="space-y-4">
                                            <div>
                                                <label className={labelClass}>Clauses Type:</label>
                                                <div className="flex flex-wrap gap-4 mt-1">
                                                    {["AUTO", "REEFER", "HAZ", "NONE"].map(val => (
                                                        <label key={val} className="flex items-center gap-1.5 text-sm cursor-pointer">
                                                            <input
                                                                type="radio"
                                                                name="clausesType"
                                                                value={val}
                                                                checked={formData?.clausesType === val}
                                                                onChange={() => handleInputChange({ target: { name: 'clausesType', value: val } })}
                                                                className="accent-rose-600"
                                                            />
                                                            <span className="text-slate-700 dark:text-slate-300">{val}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>

                                            <div className="flex flex-wrap gap-6">
                                                <label className="flex items-center gap-2 text-sm cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        name="DocumentRequestToCustomer"
                                                        checked={formData?.DocumentRequestToCustomer || false}
                                                        onChange={(e) => handleInputChange({ target: { name: 'DocumentRequestToCustomer', value: e.target.checked } })}
                                                        className="rounded accent-rose-600"
                                                    />
                                                    <span className="text-slate-700 dark:text-slate-300">Document Request To Customer</span>
                                                </label>
                                                <label className="flex items-center gap-2 text-sm cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        name="ShowAuto_BoatClause"
                                                        checked={formData?.ShowAuto_BoatClause || false}
                                                        onChange={(e) => handleInputChange({ target: { name: 'ShowAuto_BoatClause', value: e.target.checked } })}
                                                        className="rounded accent-rose-600"
                                                    />
                                                    <span className="text-slate-700 dark:text-slate-300">Show Auto/Boat Clause</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Document Dates Card */}
                                    <div className="bg-gradient-to-br from-slate-50 to-gray-50 dark:from-slate-900/50 dark:to-gray-900/20 rounded-xl border-l-2 border-l-slate-500 border border-slate-200 dark:border-slate-700 p-4 shadow-sm">
                                        <div className="flex items-center gap-2 mb-4">
                                            <div className="p-1.5 bg-slate-100 dark:bg-slate-800 rounded-lg">
                                                <Calendar className="w-4 h-4 text-slate-600 dark:text-slate-400" />
                                            </div>
                                            <h3 className="font-semibold text-slate-700 dark:text-slate-300 text-sm">Document Dates</h3>
                                        </div>
                                        <div className="space-y-3">
                                            <div>
                                                <label className={labelClass}>HBL Original Received / HBL Telex Received</label>
                                                <DateInputwithcal
                                                    onError={handleDateError}
                                                    label="HBL Original Received / HBL Telex Received"
                                                    name="OriginalReceivedDate"
                                                    value={formData?.OriginalReceivedDate || ''}
                                                    onChange={handleDateChange}
                                                    required
                                                />
                                            </div>
                                            <div>
                                                <label className={labelClass}>Telex Release Date</label>
                                                <DateInputwithcal
                                                    onError={handleDateError}
                                                    label="Telex Release Date"
                                                    name="TelexReleaseDate"
                                                    value={formData?.TelexReleaseDate || ''}
                                                    onChange={handleDateChange}
                                                    required
                                                />
                                            </div>
                                            <div>
                                                <label className={labelClass}>Telex</label>
                                                <DateInputwithcal
                                                    onError={handleDateError}
                                                    label="Telex"
                                                    name="Telex"
                                                    value={formData?.Telex || ''}
                                                    onChange={handleDateChange}
                                                    required
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Particulars Furnished by Shipper - ADDED ref */}
                <div ref={sectionRefs.Particulars} className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-700/30 dark:border-slate-700 overflow-hidden hover:shadow-md dark:shadow-none dark:hover:shadow-[0_0_12px_rgba(59,130,246,0.25)] transition-shadow duration-200">
                    <SectionHeader icon={Package} title="Particulars Furnished by Shipper" color="blue" section="Particulars" />

                    {expandedSections.Particulars && (
                        <div className="p-4 space-y-4">
                            {/* Settings & Options Panel */}
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                                {/* Print Options Card */}
                                <div className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800/50 dark:to-slate-800 rounded-xl p-4 border border-slate-200 dark:border-slate-700">
                                    <div className="flex items-center gap-2 mb-3">
                                        <div className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center">
                                            <Settings className="w-4 h-4 text-white" />
                                        </div>
                                        <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-200">Print Options</h4>
                                    </div>
                                    <div className="space-y-2">
                                        <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">Weight & Measurement on B/L</p>
                                        <div className="flex flex-wrap gap-2">
                                            {[
                                                { value: 'B', label: 'Both' },
                                                { value: 'M', label: 'Metric' },
                                                { value: 'E', label: 'English' }
                                            ].map(opt => (
                                                <label
                                                    key={opt.value}
                                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer transition-all ${formData?.WeightPrintOption === opt.value ? 'bg-blue-500 text-white shadow-md' : 'bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600 hover:border-blue-300'}`}
                                                >
                                                    <input
                                                        type="radio"
                                                        name="WeightPrintOption"
                                                        value={opt.value}
                                                        checked={formData?.WeightPrintOption === opt.value}
                                                        onChange={e => {
                                                            // Clone existing formData or fallback to empty object
                                                            const newValue = e.target.value;
                                                            handleInputChange({
                                                                target: {
                                                                    name: "WeightPrintOption",
                                                                    value: newValue,
                                                                    type: "radio"
                                                                }
                                                            });
                                                        }}
                                                        className="sr-only"
                                                    />
                                                    {opt.label}
                                                </label>
                                            ))}
                                        </div>
                                    </div>



                                </div>

                                {/* Calculation Settings Card */}
                                <div className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800/50 dark:to-slate-800 rounded-xl p-4 border border-slate-200 dark:border-slate-700">
                                    <div className="flex items-center gap-2 mb-3">
                                        <div className="w-8 h-8 rounded-lg bg-emerald-500 flex items-center justify-center">
                                            <Hash className="w-4 h-4 text-white" />
                                        </div>
                                        <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-200">Calculation</h4>
                                    </div>
                                    <div className="space-y-2">
                                        <label className="flex items-center gap-2 p-2 rounded-lg hover:bg-white dark:hover:bg-slate-700/50 transition-colors cursor-pointer">
                                            <input type="checkbox" name='DoNotAutoCalculate' checked={formData?.DoNotAutoCalculate} onChange={handleInputChange} className="w-4 h-4 text-emerald-500 rounded border-slate-300 focus:ring-emerald-500" />
                                            <span className="text-xs text-slate-600 dark:text-slate-300">Do not auto calculate</span>
                                        </label>
                                        <label className="flex items-center gap-2 p-2 rounded-lg hover:bg-white dark:hover:bg-slate-700/50 transition-colors cursor-pointer">
                                            <input type="checkbox" name='SLAC' checked={formData?.SLAC} onChange={handleInputChange} className="w-4 h-4 text-emerald-500 rounded border-slate-300 focus:ring-emerald-500" />
                                            <span className="text-xs text-slate-600 dark:text-slate-300">SLAC</span>
                                        </label>
                                    </div>
                                </div>

                                {/* Print Format Card */}
                                <div className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800/50 dark:to-slate-800 rounded-xl p-4 border border-slate-200 dark:border-slate-700">
                                    <div className="flex items-center gap-2 mb-3">
                                        <div className="w-8 h-8 rounded-lg bg-purple-500 flex items-center justify-center">
                                            <Printer className="w-4 h-4 text-white" />
                                        </div>
                                        <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-200">Print Format</h4>
                                    </div>
                                    <div className="space-y-2">
                                        <label className="flex items-center gap-2 p-2 rounded-lg hover:bg-white dark:hover:bg-slate-700/50 transition-colors cursor-pointer">
                                            <input type="checkbox" name='IsPrintGroupCommodities' checked={formData?.IsPrintGroupCommodities} onChange={handleInputChange} className="w-4 h-4 text-purple-500 rounded border-slate-300 focus:ring-purple-500" />
                                            <span className="text-xs text-slate-600 dark:text-slate-300">Print in Group Commodities</span>
                                        </label>
                                        <label className="flex items-center gap-2 p-2 rounded-lg hover:bg-white dark:hover:bg-slate-700/50 transition-colors cursor-pointer">
                                            <input type="checkbox" name='IsPrintwithRiderList' checked={formData?.IsPrintwithRiderList} onChange={handleInputChange} className="w-4 h-4 text-purple-500 rounded border-slate-300 focus:ring-purple-500" />
                                            <span className="text-xs text-slate-600 dark:text-slate-300">Print with RIDER LIST</span>
                                        </label>
                                    </div>
                                </div>
                            </div>

                            {/* Quick Actions Bar */}
                            <div className="flex flex-wrap items-center justify-between gap-3 p-3 bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 dark:from-blue-900/20 dark:via-indigo-900/20 dark:to-purple-900/20 rounded-xl border border-blue-100 dark:border-blue-800/30">
                                <div className="flex flex-wrap items-center gap-2">
                                    <span className="text-xs font-medium text-slate-500 dark:text-slate-400 mr-2">Import:</span>
                                    <button onClick={() => navigate('/booking')} className="px-3 py-1.5 bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white rounded-lg text-xs font-medium transition-all shadow-sm hover:shadow-md flex items-center gap-1.5">
                                        <Upload className="w-3 h-3" /> Dock Receipt
                                    </button>
                                    <button className="px-3 py-1.5 bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white rounded-lg text-xs font-medium transition-all shadow-sm hover:shadow-md flex items-center gap-1.5">
                                        <Upload className="w-3 h-3" /> HBL
                                    </button>
                                    <button onClick={() => navigate('/booking')} className="px-3 py-1.5 bg-gradient-to-r from-sky-500 to-blue-500 hover:from-sky-600 hover:to-blue-600 text-white rounded-lg text-xs font-medium transition-all shadow-sm hover:shadow-md flex items-center gap-1.5">
                                        <Package className="w-3 h-3" /> Booking Cargo
                                    </button>
                                </div>
                                <div className="flex flex-wrap items-center gap-2">
                                    <button className="px-3 py-1.5 bg-white dark:bg-slate-700 hover:bg-slate-50 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 rounded-lg text-xs font-medium transition-all border border-slate-200 dark:border-slate-600 flex items-center gap-1.5">
                                        <FileText className="w-3 h-3" /> Fill Description
                                    </button>
                                    <button className="px-3 py-1.5 bg-white dark:bg-slate-700 hover:bg-slate-50 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 rounded-lg text-xs font-medium transition-all border border-slate-200 dark:border-slate-600 flex items-center gap-1.5">
                                        <Settings className="w-3 h-3" /> Override
                                    </button>
                                </div>
                            </div>

                            {/* Cargo Grid */}
                            <div className="rounded-xl border border-slate-200 dark:border-slate-700">
                                {/* Grid Header */}
                                <div className="grid bg-gradient-to-r from-slate-700 to-slate-800 dark:from-slate-800 dark:to-slate-900 text-white text-xs font-semibold" style={{ gridTemplateColumns: '120px 100px 160px 1fr 160px 120px 120px 120px 60px' }}>
                                    <div className="px-3 py-3 text-center border-r border-slate-600">CONTAINER</div>
                                    <div className="px-3 py-3 text-center border-r border-slate-600">CONT. TYPE</div>
                                    <div className="px-3 py-3 text-center border-r border-slate-600">NO. OF PKG</div>
                                    <div className="px-3 py-3 text-center border-r border-slate-600">CARGO DESCRIPTION</div>
                                    <div className="px-3 py-3 text-center border-r border-slate-600">IT # / CN/CPRS # / Pickup # / LAST FREE DATE</div>
                                    <div className="px-3 py-3 text-center border-r border-slate-600">NET WEIGHT</div>
                                    <div className="px-3 py-3 text-center border-r border-slate-600">GROSS WEIGHT</div>
                                    <div className="px-3 py-3 text-center border-r border-slate-600">MEASUREMENT</div>
                                    <div className="px-3 py-3 text-center">DEL</div>
                                </div>

                                {/* Grid Rows */}
                                <div className=" bg-white dark:bg-slate-900">
                                    <div className="min-w-[1000px]">
                                        {rows.map((row, index) => row?.IsDeleted == false && (
                                            <div key={index} className="grid border-t border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors" style={{ gridTemplateColumns: '120px 100px 160px 1fr 160px 120px 120px 120px 60px' }}>
                                                {/* CONTAINER - Column 1 */}
                                                <div className="p-2 space-y-1.5 border-r border-slate-100 dark:border-slate-700/50">
                                                    <input
                                                        value={row?.ContainerNumber}
                                                        onChange={(e) => _handleInputChange(index, 'ContainerNumber', e.target.value)}
                                                        placeholder="Container No"
                                                        className="w-full h-7 rounded-md border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 px-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                                    />
                                                    <input
                                                        value={row?.SealNumber}
                                                        onChange={(e) => _handleInputChange(index, 'SealNumber', e.target.value)}
                                                        placeholder="Seal No"
                                                        className="w-full h-7 rounded-md border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 px-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                                    />
                                                    <textarea
                                                        rows={6}
                                                        value={row?.MarksNo}
                                                        onChange={(e) => _handleInputChange(index, 'MarksNo', e.target.value)}
                                                        placeholder="Marks No"
                                                        className="w-full min-h-[70px] resize-y rounded-md border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                                    />
                                                </div>

                                                {/* CONT TYPE - Column 2 */}
                                                <div className="p-2 space-y-1.5 border-r border-slate-100 dark:border-slate-700/50">
                                                    <select
                                                        value={row?.ContTypeCode || ''}
                                                        onChange={(e) => {
                                                            _handleInputChange(index, 'ContTypeCode', e.target.value)
                                                            _handleInputChange(index, 'ContTypeName', e.target.options[e.target.selectedIndex].text)
                                                        }}
                                                        className="w-full h-7 rounded-md border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 px-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                                    >
                                                        <option value="">Select</option>
                                                        {
                                                            containerTypeList?.map((container, key) => (
                                                                <option key={key} value={container.moveTypeId}>{container.moveType}</option>
                                                            ))
                                                        }
                                                    </select>
                                                </div>

                                                {/* NO OF PKG - Column 3 */}
                                                <div className="p-2 space-y-1.5 border-r border-slate-100 dark:border-slate-700/50">
                                                    <div className="grid grid-cols-2 gap-1">
                                                        <input
                                                            value={row?.NoPackage}
                                                            onChange={(e) => _handleInputChange(index, 'NoPackage', e.target.value)}
                                                            placeholder="Qty"
                                                            className="w-full h-7 rounded-md border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 px-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                                        />
                                                        <select
                                                            value={row?.PackageCode}
                                                            onChange={(e) => _handleInputChange(index, 'PackageCode', e.target.value)}
                                                            className="w-full h-7 rounded-md border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 px-1 text-[10px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                                        >
                                                            <option value="">Type</option>
                                                            {/* <option value="CARTON">CTN</option>
                                                            <option value="PALLET">PLT</option>
                                                            <option value="CRATE">CRT</option>
                                                            <option value="DRUM">DRM</option>
                                                            <option value="BAG">BAG</option> */}
                                                            {
                                                                packageTypeList?.map((item, index) => (
                                                                    <option key={index} value={item.package}>{item.package}</option>
                                                                ))
                                                            }
                                                        </select>
                                                    </div>

                                                    <input
                                                        value={row?.HAZCode}
                                                        onChange={(e) => _handleInputChange(index, 'HAZCode', e.target.value)}
                                                        placeholder="HS Code"
                                                        className="w-full h-7 rounded-md border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 px-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                                    />

                                                    <input
                                                        value={row?.VIN_NUMBER}
                                                        name="VIN_NUMBER"
                                                        onChange={(e) => _handleInputChange(index, 'VIN_NUMBER', e.target.value)}
                                                        placeholder="VIN Number"
                                                        className="w-full h-7 rounded-md border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 px-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                                    />

                                                    <button className="w-full h-7 rounded-md bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white text-[10px] font-medium transition-all shadow-sm flex items-center justify-center gap-1">
                                                        <Check className="w-3 h-3" /> Validate VIN
                                                    </button>

                                                    <div className="grid grid-cols-2 gap-1">
                                                        <input
                                                            value={row?.TITLE_NUMBER}
                                                            name="TITLE_NUMBER"
                                                            onChange={(e) => _handleInputChange(index, 'TITLE_NUMBER', e.target.value)}
                                                            placeholder="Title No"
                                                            className="w-full h-7 rounded-md border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 px-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                                        />
                                                        <input
                                                            value={row.CARGO_STATE}
                                                            name="CARGO_STATE"
                                                            onChange={(e) => _handleInputChange(index, 'CARGO_STATE', e.target.value)}
                                                            placeholder="State"
                                                            className="w-full h-7 rounded-md border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 px-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                                        />
                                                    </div>
                                                    <SearchBarPagination
                                                        searchAPI="https://api.shipez.ai/api/v1/AutoSuggestion/GetSupplierSuggestion?Prefix="
                                                        value={row?.Manufacturer || ''}
                                                        placeholder="Select Manufacturer"
                                                        name="Manufacturer"
                                                        selectRecord={(d) => {
                                                            _handleInputChange(index, 'Manufacturer', d?.[1]);
                                                        }}
                                                        onChangeVal={(val) => {
                                                            if (!val) {
                                                                _handleInputChange(index, 'Manufacturer', '');
                                                            }
                                                        }}
                                                    />


                                                    <select className="w-full h-7 rounded-md border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 px-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                                        value={row?.Country || ''}
                                                        onChange={(e) => _handleInputChange(index, 'Country', e.target.value)}
                                                        name="Country"
                                                    >
                                                        <option>select Country</option>
                                                        {
                                                            countryList?.map((country, key) => (
                                                                <option key={key} value={country.incoterm}>{country.incoterm}</option>
                                                            ))
                                                        }
                                                    </select>
                                                </div>

                                                {/* CARGO DESCRIPTION - Column 4 (Wider) */}
                                                <div className="p-2 border-r border-slate-100 dark:border-slate-700/50">
                                                    <textarea
                                                        rows={8}
                                                        value={row.CargoDescription}
                                                        onChange={(e) => _handleInputChange(index, 'CargoDescription', e.target.value)}
                                                        placeholder="Enter detailed cargo description here...&#10;&#10;Example:&#10;- 100 CARTONS OF ELECTRONICS&#10;- HS CODE: 8471.30&#10;- MADE IN CHINA"
                                                        className="w-full h-full min-h-[180px] resize-y rounded-md border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 px-3 py-2 text-xs leading-relaxed focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                                    />
                                                </div>
                                                {/* IT # / CN/CPRS # / Pickup # / LAST FREE DATE */}
                                                <div className="p-2 space-y-2 border-r border-slate-100 dark:border-slate-700/50">
                                                    <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-2 space-y-1.5">
                                                        <input
                                                            value={row.ITNumber}
                                                            onChange={(e) => _handleInputChange(index, 'ITNumber', e.target.value)}
                                                            placeholder='IT No.'
                                                            className="w-full h-7 rounded-md border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 px-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                                        />

                                                        <input
                                                            value={row.CNCPRSNumber}
                                                            onChange={(e) => _handleInputChange(index, 'CNCPRSNumber', e.target.value)}
                                                            placeholder='CN/CPRS No.'
                                                            className="w-full h-7 rounded-md border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 px-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                                        />
                                                        <input
                                                            value={row.PickUpNumber}
                                                            onChange={(e) => _handleInputChange(index, 'PickUpNumber', e.target.value)}
                                                            placeholder='Pickup No.'
                                                            className="w-full h-7 rounded-md border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 px-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                                        />

                                                        <DateInputwithcal onError={handleDateError} label="Production Date [Start Date]" name="LastDate"
                                                            value={row?.LastDate}
                                                            onChange={(name, value) => _handleInputChange(index, name, value)}
                                                            required />
                                                    </div>
                                                </div>

                                                {/* NET WEIGHT - Column 5 */}
                                                <div className="p-2 space-y-2 border-r border-slate-100 dark:border-slate-700/50">
                                                    <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-2 space-y-1.5">
                                                        <label className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Net KGS</label>
                                                        <input
                                                            value={row.NetKgs}
                                                            onChange={(e) => handleWeightChange(index, 'NetKgs', e.target.value)}
                                                            className="w-full h-7 rounded-md border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 px-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                                        />
                                                    </div>
                                                    <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-2 space-y-1.5">
                                                        <label className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Net LBS</label>
                                                        <input
                                                            value={row.NetLbs}
                                                            onChange={(e) => handleWeightChange(index, 'NetLbs', e.target.value)}
                                                            className="w-full h-7 rounded-md border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 px-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                                        />
                                                    </div>
                                                </div>

                                                {/* GROSS WEIGHT - Column 6 */}
                                                <div className="p-2 space-y-2 border-r border-slate-100 dark:border-slate-700/50">
                                                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-2 space-y-1.5">
                                                        <label className="text-[10px] font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wide">KGS</label>
                                                        <input
                                                            value={row.KGS}
                                                            onChange={(e) => handleWeightChange(index, 'KGS', e.target.value)}
                                                            className="w-full h-7 rounded-md border border-blue-200 dark:border-blue-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 px-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                                        />
                                                    </div>
                                                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-2 space-y-1.5">
                                                        <label className="text-[10px] font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wide">LBS</label>
                                                        <input
                                                            value={row.LBS}
                                                            onChange={(e) => handleWeightChange(index, 'LBS', e.target.value)}
                                                            className="w-full h-7 rounded-md border border-blue-200 dark:border-blue-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 px-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                                        />
                                                    </div>
                                                </div>

                                                {/* MEASUREMENT - Column 7 */}
                                                <div className="p-2 space-y-2 border-r border-slate-100 dark:border-slate-700/50">
                                                    <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-2 space-y-1.5">
                                                        <label className="text-[10px] font-semibold text-purple-600 dark:text-purple-400 uppercase tracking-wide">CBM</label>
                                                        <input
                                                            value={row.CBM}
                                                            onChange={(e) => handleWeightChange(index, 'CBM', e.target.value)}
                                                            className="w-full h-7 rounded-md border border-purple-200 dark:border-purple-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 px-2 text-xs focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                                                        />
                                                    </div>
                                                    <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-2 space-y-1.5">
                                                        <label className="text-[10px] font-semibold text-purple-600 dark:text-purple-400 uppercase tracking-wide">CFT</label>
                                                        <input
                                                            value={row.CFT}
                                                            onChange={(e) => handleWeightChange(index, 'CFT', e.target.value)}
                                                            className="w-full h-7 rounded-md border border-purple-200 dark:border-purple-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 px-2 text-xs focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                                                        />
                                                    </div>
                                                </div>

                                                {/* DELETE - Column 8 */}
                                                <div className="p-2 flex items-center justify-center">
                                                    <button
                                                        onClick={() => { _handleInputChange(index, 'IsDeleted', true) }}
                                                        className="w-9 h-9 flex items-center justify-center rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/40 hover:text-red-600 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                                                        disabled={rows.length === 1}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Add More Row Button */}
                                <div className="bg-gradient-to-r from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-700 py-3 flex justify-center border-t border-slate-200 dark:border-slate-700">
                                    <button
                                        onClick={addRow}
                                        className="px-5 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white rounded-lg font-medium text-sm transition-all shadow-md hover:shadow-lg flex items-center gap-2"
                                    >
                                        <Plus className="w-4 h-4" /> Add Container Row
                                    </button>
                                </div>
                            </div>

                            {/* Totals Summary */}
                            <div className="bg-gradient-to-br from-slate-50 to-white dark:from-slate-800/50 dark:to-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
                                <div className="flex items-center gap-2 mb-4">
                                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center">
                                        <Weight className="w-4 h-4 text-white" />
                                    </div>
                                    <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-200">Total Summary</h4>
                                </div>

                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    <div className="bg-white dark:bg-slate-800 rounded-lg p-3 border border-slate-100 dark:border-slate-700">
                                        <p className="text-[10px] text-slate-400 dark:text-slate-500 uppercase tracking-wide mb-1">Containers</p>
                                        <p className="text-lg font-bold text-slate-800 dark:text-slate-200">{formData?.TotalContainer || '0'}</p>
                                    </div>
                                    <div className="bg-white dark:bg-slate-800 rounded-lg p-3 border border-slate-100 dark:border-slate-700">
                                        <p className="text-[10px] text-slate-400 dark:text-slate-500 uppercase tracking-wide mb-1">Packages</p>
                                        <p className="text-lg font-bold text-slate-800 dark:text-slate-200">{formData?.TotalPackage || '0'}</p>
                                    </div>
                                    <div className="bg-white dark:bg-slate-800 rounded-lg p-3 border border-slate-100 dark:border-slate-700">
                                        <p className="text-[10px] text-slate-400 dark:text-slate-500 uppercase tracking-wide mb-1">Gross KGS</p>
                                        <p className="text-lg font-bold text-slate-800 dark:text-slate-200">{formData?.TotalKgs || '0.00'}</p>
                                    </div>
                                    <div className="bg-white dark:bg-slate-800 rounded-lg p-3 border border-slate-100 dark:border-slate-700">
                                        <p className="text-[10px] text-slate-400 dark:text-slate-500 uppercase tracking-wide mb-1">Gross LBS</p>
                                        <p className="text-lg font-bold text-slate-800 dark:text-slate-200">{formData?.TotalLbs || '0.00'}</p>
                                    </div>
                                    <div className="bg-white dark:bg-slate-800 rounded-lg p-3 border border-slate-100 dark:border-slate-700">
                                        <p className="text-[10px] text-slate-400 dark:text-slate-500 uppercase tracking-wide mb-1">Net KGS</p>
                                        <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">{formData?.totalNetKgs || '0.00'}</p>
                                    </div>
                                    <div className="bg-white dark:bg-slate-800 rounded-lg p-3 border border-slate-100 dark:border-slate-700">
                                        <p className="text-[10px] text-slate-400 dark:text-slate-500 uppercase tracking-wide mb-1">Net LBS</p>
                                        <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">{formData?.totalNetLbs || '0.00'}</p>
                                    </div>
                                    <div className="bg-white dark:bg-slate-800 rounded-lg p-3 border border-slate-100 dark:border-slate-700">
                                        <p className="text-[10px] text-slate-400 dark:text-slate-500 uppercase tracking-wide mb-1">CBM</p>
                                        <p className="text-lg font-bold text-blue-600 dark:text-blue-400">{formData?.TotalCbm || '0.00'}</p>
                                    </div>
                                    <div className="bg-white dark:bg-slate-800 rounded-lg p-3 border border-slate-100 dark:border-slate-700">
                                        <p className="text-[10px] text-slate-400 dark:text-slate-500 uppercase tracking-wide mb-1">CFT</p>
                                        <p className="text-lg font-bold text-blue-600 dark:text-blue-400">{formData?.TotalCft || '0.00'}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* AR & AP Schema / Charges - ADDED ref */}
                <div ref={sectionRefs.rateCharges}>
                    <ARAPChargesContainer
                        bookingId={isEditMode ? idbooking : null}
                        bookingNo={formData?.fileRefNumber}
                        arCharges={arCharges}
                        setArCharges={setArCharges}
                        apCharges={apCharges}
                        setApCharges={setApCharges}
                        currencyList={currency_list}
                        basisList={charges_basis_list}
                        containerList={containerTypeList}
                        chargeCodeList={[]}
                        customerId={formData?.shipperId}
                        customerName={formData?.customerName || formData?.shipperExporter}
                    />
                </div>

                {/* P/L Synopsis */}
                <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-700/30 dark:border-slate-700 p-4 hover:shadow-md dark:shadow-none dark:hover:shadow-[0_0_12px_rgba(59,130,246,0.25)] transition-shadow duration-200">
                    <div className="flex items-center gap-3 mb-3"><div className="p-2 rounded-lg bg-emerald-500 shadow-md"><DollarSign className="w-4 h-4 text-white" /></div><h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200">P/L Synopsis</h2></div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4">
                        <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg"><p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Credit</p><p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">${formData?.credit}</p></div>
                        <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg"><p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Debit</p><p className="text-lg font-bold text-red-600 dark:text-red-400">${formData?.debit}</p></div>
                        <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg"><p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Handling</p><p className="text-lg font-bold text-blue-600 dark:text-blue-400">${formData?.handling}</p></div>
                        <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg"><p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Profit</p><p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">${formData?.profit}</p></div>
                        <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg col-span-2"><p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Profit Margin</p><p className="text-lg font-bold text-indigo-600 dark:text-indigo-400">{formData?.profitMargin}%</p></div>
                    </div>
                </div>

                {/* Remarks */}
                <div ref={sectionRefs.remarks} className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-700/30 dark:border-slate-700 overflow-hidden hover:shadow-md dark:shadow-none dark:hover:shadow-[0_0_12px_rgba(59,130,246,0.25)] transition-shadow duration-200">
                    <SectionHeader icon={FileText} title="Remarks" color="pink" section="remarks" />
                    {expandedSections.remarks && (
                        <div className="p-4 border-t border-slate-200 dark:border-slate-800">
                            {/* Remarks Textareas - 3 columns */}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                                {/* Customer Remarks */}
                                <div className="bg-gradient-to-br from-pink-50 to-rose-50 dark:from-pink-950/20 dark:to-rose-950/10 rounded-xl border-l-2 border-l-pink-500 border border-slate-200 dark:border-slate-700 p-3 shadow-sm">
                                    <div className="flex items-center gap-2 mb-2">
                                        <div className="p-1 bg-pink-100 dark:bg-pink-900/30 rounded-lg">
                                            <FileText className="w-3.5 h-3.5 text-pink-600 dark:text-pink-400" />
                                        </div>
                                        <label className="font-semibold text-pink-700 dark:text-pink-400 text-xs">Customer Remarks</label>
                                    </div>
                                    <textarea
                                        name="RateRemarks"
                                        value={formData?.RateRemarks || ''}
                                        onChange={handleInputChange}
                                        rows={6}
                                        className={`${inputClass} resize-none w-full`}
                                        placeholder="Enter customer remarks..."
                                    />
                                </div>

                                {/* Internal Remarks */}
                                <div className="bg-gradient-to-br from-slate-50 to-gray-50 dark:from-slate-900/50 dark:to-gray-900/20 rounded-xl border-l-2 border-l-slate-500 border border-slate-200 dark:border-slate-700 p-3 shadow-sm">
                                    <div className="flex items-center gap-2 mb-2">
                                        <div className="p-1 bg-slate-100 dark:bg-slate-800 rounded-lg">
                                            <FileCheck className="w-3.5 h-3.5 text-slate-600 dark:text-slate-400" />
                                        </div>
                                        <label className="font-semibold text-slate-700 dark:text-slate-300 text-xs">Internal Remarks</label>
                                    </div>
                                    <textarea
                                        name="InternalNotes"
                                        value={formData?.InternalNotes || ''}
                                        onChange={handleInputChange}
                                        rows={6}
                                        className={`${inputClass} resize-none w-full`}
                                        placeholder="Enter internal remarks..."
                                    />
                                </div>

                                {/* Delivery Remarks */}
                                <div className="bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-950/20 dark:to-yellow-950/10 rounded-xl border-l-2 border-l-amber-500 border border-slate-200 dark:border-slate-700 p-3 shadow-sm">
                                    <div className="flex items-center gap-2 mb-2">
                                        <div className="p-1 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                                            <Truck className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
                                        </div>
                                        <label className="font-semibold text-amber-700 dark:text-amber-400 text-xs">Delivery Remarks</label>
                                    </div>
                                    <textarea
                                        name="DeliveryRemarks"
                                        value={formData?.DeliveryRemarks || ''}
                                        onChange={handleInputChange}
                                        rows={6}
                                        className={`${inputClass} resize-none w-full`}
                                        placeholder="Enter delivery remarks..."
                                    />
                                </div>
                            </div>

                            {/* Freight Payable, Place of Issue, Issue Date - 3 columns */}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                                {/* Freight Payable at */}
                                <div className="bg-gradient-to-br from-indigo-50 to-blue-50 dark:from-indigo-950/20 dark:to-blue-950/10 rounded-xl border-l-2 border-l-indigo-500 border border-slate-200 dark:border-slate-700 p-3 shadow-sm">
                                    <div className="flex items-center gap-2 mb-2">
                                        <div className="p-1 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                                            <DollarSign className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" />
                                        </div>
                                        <label className="font-semibold text-indigo-700 dark:text-indigo-400 text-xs">Freight Payable at</label>
                                    </div>
                                    <SearchBarPagination
                                        searchAPI={'v1/AutoSuggestion/GetDestinationSuggestion?Prefix='}
                                        selectReacord={(d) => {
                                            handleInputChange({ target: { name: 'Freight_Payable', value: d?.[1] } });
                                            handleInputChange({
                                                target: {
                                                    name: 'Freight_PayableCode',
                                                    value: _getPortCode(d?.[1])
                                                }
                                            })
                                        }}
                                        value={formData?.Freight_Payable || ''}
                                        onchange={(val) => {
                                            if (!val) {
                                                handleInputChange({ target: { name: 'Freight_Payable', value: '' } });
                                            }
                                        }}
                                    />
                                </div>

                                {/* Place of Issue */}
                                <div className="bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-950/20 dark:to-violet-950/10 rounded-xl border-l-2 border-l-purple-500 border border-slate-200 dark:border-slate-700 p-3 shadow-sm">
                                    <div className="flex items-center gap-2 mb-2">
                                        <div className="p-1 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                                            <MapPin className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400" />
                                        </div>
                                        <label className="font-semibold text-purple-700 dark:text-purple-400 text-xs">Place of Issue</label>
                                    </div>
                                    <SearchBarPagination
                                        searchAPI={'v1/AutoSuggestion/GetDestinationSuggestion?Prefix='}
                                        selectReacord={(d) => {
                                            handleInputChange({ target: { name: 'PlaceOfIssue', value: d?.[1] } });
                                            handleInputChange({
                                                target: {
                                                    name: 'PlaceOfIssueCode',
                                                    value: _getPortCode(d?.[1])
                                                }
                                            })
                                        }}
                                        value={formData?.PlaceOfIssue || ''}
                                        onchange={(val) => {
                                            if (!val) {
                                                handleInputChange({ target: { name: 'PlaceOfIssue', value: '' } });
                                            }
                                        }}
                                    />
                                </div>

                                {/* Issue Date */}
                                <div className="bg-gradient-to-br from-cyan-50 to-teal-50 dark:from-cyan-950/20 dark:to-teal-950/10 rounded-xl border-l-2 border-l-cyan-500 border border-slate-200 dark:border-slate-700 p-3 shadow-sm">
                                    <div className="flex items-center gap-2 mb-2">
                                        <div className="p-1 bg-cyan-100 dark:bg-cyan-900/30 rounded-lg">
                                            <Calendar className="w-3.5 h-3.5 text-cyan-600 dark:text-cyan-400" />
                                        </div>
                                        <label className="font-semibold text-cyan-700 dark:text-cyan-400 text-xs">Issue Date</label>
                                    </div>
                                    <DateInputwithcal
                                        onError={handleDateError}
                                        label="Issue Date"
                                        name="IssueDate"
                                        value={formData?.IssueDate ? new Date(formData?.IssueDate) : null}
                                        onChange={handleDateChange}
                                        required
                                    />
                                </div>
                            </div>

                            {/* BL Sign - full width row */}
                            <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/10 rounded-xl border-l-2 border-l-green-500 border border-slate-200 dark:border-slate-700 p-3 shadow-sm">
                                <div className="flex items-center gap-2 mb-2">
                                    <div className="p-1 bg-green-100 dark:bg-green-900/30 rounded-lg">
                                        <FileSignature className="w-3.5 h-3.5 text-green-600 dark:text-green-400" />
                                    </div>
                                    <label htmlFor="BLsign" className="font-semibold text-green-700 dark:text-green-400 text-xs">BL Sign</label>
                                </div>
                                <Input
                                    id="BLsign"
                                    type="text"
                                    name="BLSignature"
                                    value={formData?.BLSignature || ''}
                                    onChange={handleInputChange}
                                    placeholder="Enter BL Sign..."
                                    className={inputClass}
                                />
                            </div>
                        </div>
                    )}
                </div>

                {/* document upload section */}
                <div ref={sectionRefs.documentsUpload} className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-700/30 dark:border-slate-700 overflow-hidden mt-2 hover:shadow-md dark:shadow-none dark:hover:shadow-[0_0_12px_rgba(59,130,246,0.25)] transition-shadow duration-200">
                    <SectionHeader icon={Paperclip} title="Document & Attachments" color="amber" section="documentsupload" />
                    {expandedSections.documentsupload && (
                        <div className="p-4 border-t border-slate-200 dark:border-slate-800">
                            {Number(formData?.BolID) > 0 ? (
                                !formData?.CarrierBookingNo ? (
                                    <div className="text-red-500 text-sm font-medium">
                                        Please enter Carrier Booking Number to upload documents.
                                    </div>
                                ) : (
                                    <FileUpload
                                        orderId={formData?.BolID}
                                        orderNum={formData?.FileRefNumber}
                                        type="ImportBooking"
                                    />
                                )
                            ) : (
                                <div className="text-red-500 text-sm font-medium">
                                    Please save the booking first to access document uploads.
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Notes upload section */}
                <div ref={sectionRefs.notesUpload} className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-700/30 dark:border-slate-700 overflow-hidden mt-2 hover:shadow-md dark:shadow-none dark:hover:shadow-[0_0_12px_rgba(59,130,246,0.25)] transition-shadow duration-200">
                    <SectionHeader icon={Edit2} title="Notes" color="green" section="NotesUpload" />
                    {expandedSections.NotesUpload && (
                        <div className="p-4 border-t border-slate-200 dark:border-slate-800">
                            {Number(formData?.BolID) > 0 ? (
                                <div className="p-4">
                                    <UploadNotes
                                        BookingID={formData?.BolID}
                                        RefNumber={formData?.FileRefNumber}
                                        type="ImportBooking"
                                    />
                                </div>
                            ) : (
                                <div className="p-4 text-red-500 text-sm font-medium">
                                    Please save the booking first to access uploads notes.
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Common Clause Section */}
                <div ref={sectionRefs.commonClause} className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-700/30 dark:border-slate-700 overflow-hidden mt-3 hover:shadow-md dark:shadow-none dark:hover:shadow-[0_0_12px_rgba(59,130,246,0.25)] transition-shadow duration-200">
                    <SectionHeader icon={FileText} title="Clause" color="cyan" section="commonClause" />
                    {expandedSections.commonClause && (
                        <div className="border-t border-slate-200 dark:border-slate-800">
                            {/* Header */}
                            <div className="grid grid-cols-12 gap-4 px-4 py-3 bg-slate-50 dark:bg-slate-800 sticky top-0 z-10">
                                <div className="col-span-1 text-center text-xs font-semibold uppercase text-slate-500">Apply</div>
                                <div className="col-span-3 text-xs font-semibold uppercase text-slate-500">Clause Type</div>
                                <div className="col-span-6 text-xs font-semibold uppercase text-slate-500">Clause Description</div>
                                <div className="col-span-2 text-right text-xs font-semibold uppercase text-slate-500">Status</div>
                            </div>

                            {/* Scrollable Body */}
                            <div className="max-h-[240px] overflow-y-auto divide-y divide-slate-200 dark:divide-slate-700">
                                {clauses?.length > 0 ? (
                                    clauses.map((clause, index) => (
                                        <div key={`clause-${clause.ClauseTypeDescriptionID}-${index}`} className="grid grid-cols-12 gap-4 px-4 py-3 items-center hover:bg-slate-50 dark:hover:bg-slate-800 transition">
                                            <div className="col-span-1 flex justify-center">
                                                <input type="checkbox" className="h-3 w-3 rounded border-slate-300 text-[#0072FF] focus:ring-[#0072FF]" checked={clause.applied || false} onChange={(e) => handleClauseChange(clause.ClauseTypeDescriptionID, 'applied', e.target.checked)} />
                                            </div>
                                            <div className="col-span-3 text-xs font-medium text-slate-800 dark:text-slate-100">{clause.ClauseType}</div>
                                            <div className="col-span-6 relative group">
                                                <p className="text-xs text-slate-600 dark:text-slate-400 truncate cursor-pointer">{clause.ClauseDescription}</p>
                                                <div className="absolute left-0 top-full z-20 hidden group-hover:block w-[420px] p-3 mt-2 text-sm text-white bg-slate-900 rounded-md shadow-lg">{clause.ClauseDescription}</div>
                                            </div>
                                            <div className="col-span-2 flex justify-end">
                                                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${clause.applied ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'}`}>
                                                    {clause.applied ? 'Applied' : 'Not Applied'}
                                                </span>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="py-6 text-center text-sm text-slate-500">Loading clauses...</div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Bottom Actions */}
                <div className="p-3 bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800 overflow-hidden hover:shadow-md dark:shadow-none dark:hover:shadow-[0_0_12px_rgba(59,130,246,0.25)] transition-shadow duration-200">
                    <div className="flex items-center justify-between">
                        <div className="text-sm text-slate-500 dark:text-slate-400">
                            <span className="text-red-500">*</span> Required fields must be filled
                        </div>
                        <div className="flex items-center gap-1.5">
                            <button onClick={() => navigate('/import-booking')} className="inline-flex items-center gap-1.5 text-[12.5px] font-medium px-4 h-[40px] rounded-[9px] transition-all duration-150 text-slate-600 hover:bg-red-50">
                                <span className="w-[15px] h-[15px] flex-shrink-0 [&>svg]:w-full [&>svg]:h-full"><X /></span>
                                <span className="hidden sm:inline">Cancel</span>
                            </button>

                            {Number(formData?.BolID) > 0 && (
                                <button type="button" onClick={() => setPrintModalOpen(true)} className="inline-flex items-center gap-1.5 text-[12.5px] font-medium px-4 h-[40px] rounded-[9px] transition-all duration-150 bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-200 dark:border-slate-600 dark:hover:bg-slate-600">
                                    <span className="w-[15px] h-[15px] flex-shrink-0 [&>svg]:w-full [&>svg]:h-full"><Printer /></span>
                                    <span className="hidden sm:inline">Print</span>
                                </button>
                            )}

                            <div className="w-px h-6 bg-slate-200 mx-1" />

                            <CelebrationButton onClick={() => handleSubmit()} label="Submit Booking" isSuccess={isAnimate} className="inline-flex items-center gap-1.5 text-[12.5px] font-medium px-4 h-[40px] rounded-[9px] transition-all duration-150 bg-green-500 text-white hover:bg-green-700" icon={<span className="w-[15px] h-[15px] flex-shrink-0 [&>svg]:w-full [&>svg]:h-full"><Send /></span>} />
                        </div>
                    </div>
                </div>
            </form>

            <PrintDocumentsSelectionModal isOpen={printModalOpen} onClose={() => setPrintModalOpen(false)} documents={importPrintDocuments} onPrint={handleImportPrintDocuments} />
        </div>
    );
};

export default ImportBookingForm;